import React, { useEffect, useState, useMemo, useRef } from "react";
import styles from "../assets/css/UserList.module.css";
import { defaultProfile, togleA, togleB, view } from "../assets/img";
import { NavLink, useNavigate } from "react-router-dom";
import { postData1 } from "../../services";
import { Loader } from "../../components/Loader";
import { prev_arrow, next_arrow } from "../../assets/images";
import { DownloadToExcel, Pagination } from "../Common";
import { AlertPopup } from "../Common";
import ProductDetailsModal from "../../components/ProductDetailsModal";
import ProductVisitedList from "./Dashboard/ProductVisitedList";
import ProductWishlistList from "./Dashboard/ProductWishlistList";
import { UserListPagination } from "./UserListPagination";
import UserRegisterAdmin from "./UserRegisterAdmin";
import { useDebounce } from "@uidotdev/usehooks";

export const UserList = () => {
  const [userlistData, setUserListData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currPage, setCurrPage] = useState(1);
  const [paging, setPaging] = useState({});
  const [prevPage, setPrevPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "created_date",
    direction: "desc",
  });
  const [perPage, setPerPage] = useState(50);

  // ---- Filters ----
  const [sourceFilter, setSourceFilter] = useState([]);
  const [customerTypeFilter, setCustomerTypeFilter] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateFilterType, setDateFilterType] = useState("created_date");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lastActivityFilter, setLastActivityFilter] = useState([]);
  const [mobileFilter, setMobileFilter] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  // ---- Debounced text filters ----
  const debouncedMobile = useDebounce(mobileFilter, 400);
  const debouncedName = useDebounce(customerNameFilter, 400);
  const debouncedEmail = useDebounce(emailFilter, 400);

  const [cartMin, setCartMin] = useState("");
  const [cartMax, setCartMax] = useState("");
  const [qtyMin, setQtyMin] = useState("");
  const [qtyMax, setQtyMax] = useState("");

  // ---- Toggle visibility of inline filter inputs ----
  const [showFilter, setShowFilter] = useState({
    mobile: false,
    customerName: false,
    email: false,
    cart: false,
    qty: false,
  });

  // ---- Dropdown toggles ----
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showCustomerTypeDropdown, setShowCustomerTypeDropdown] = useState(false);
  const [showUserTypeDropdown, setShowUserTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showLastActivityDropdown, setShowLastActivityDropdown] = useState(false);

  const sourceDropdownRef = useRef(null);
  const customerTypeDropdownRef = useRef(null);
  const userTypeDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const lastActivityDropdownRef = useRef(null);

  // ---- Column visibility ----
  const ALL_COLUMNS = [
    { label: "S.No", key: "sno" },
    { label: "Source", key: "source" },
    { label: "Account Created", key: "created_date" },
    { label: "User ID (Mobile No.)", key: "mobile" },
    { label: "Customer Name", key: "first_name" },
    { label: "Email ID", key: "email" },
    { label: "Customer Type", key: "profile_type" },
    { label: "Purpose", key: "last_activity" },
    { label: "Last Visited", key: "last_visit" },
    { label: "Speciality", key: "speciality" },
    { label: "Location", key: "location" },
    { label: "Last Login", key: "last_login" },
    { label: "Recent Action", key: "recent_action" },
    { label: "Pop up URL", key: "popup_url" },
    { label: "Need Help", key: "need_help" },
    { label: "Brochure Download", key: "catalog" },
    { label: "Arrange Demo", key: "demo" },
    { label: "SM Outbound", key: "sm_outbound" },
    { label: "Add to Cart", key: "cart" },
    { label: "Checkout Cart", key: "checkout" },
    { label: "Recently Viewed Products", key: "recently_viewed_product_count" },
    { label: "Wishlist", key: "wishlist_count" },
    { label: "Communication History", key: "communication_history" },
    { label: "Qty", key: "total_products_purchased" },
    { label: "Status", key: "status" },
    { label: "Action", key: "action" },
  ];
  const STORAGE_KEY = "userlist_visible_columns";
  const DEFAULT_VISIBLE = [
    "S.No",
    "User ID (Mobile No.)",
    "Customer Name",
    "Email ID",
    "Customer Type",
    "Communication History",
    "Status",
    "Action",
  ];
  const COMMUNICATION_COLUMN_MIGRATION_KEY =
    "userlist_communication_history_column_v1";

  const getInitialColumns = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const validLabels = ALL_COLUMNS.map((c) => c.label);
        const filtered = parsed.filter((l) => validLabels.includes(l));
        if (filtered.length > 0) {
          if (!localStorage.getItem(COMMUNICATION_COLUMN_MIGRATION_KEY)) {
            if (!filtered.includes("Communication History")) {
              filtered.push("Communication History");
            }
            localStorage.setItem(COMMUNICATION_COLUMN_MIGRATION_KEY, "true");
          }
          return filtered;
        }
      }
      localStorage.setItem(COMMUNICATION_COLUMN_MIGRATION_KEY, "true");
    } catch (e) {
      // ignore
    }
    return DEFAULT_VISIBLE;
  };

  const [visibleColumns, setVisibleColumns] = useState(getInitialColumns);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorRef = useRef(null);

  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedUserUid, setSelectedUserUid] = useState("");
  const [modalType, setModalType] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [activityModal, setActivityModal] = useState(null);
  const [communicationModal, setCommunicationModal] = useState(null);

  const [downloadData, setDownloadData] = useState([]);
  const navigate = useNavigate();
  const [isUserRegOpen, setIsUserRegOpen] = useState(false);

  // ---- Helpers ----
  const resetPage = () => {
    setCurrPage(1);
    setPrevPage(1);
  };

  const formatSourceLabel = (source) => {
    if (!source) return "";
    return source === "direct" ? "Equipmedy" : source;
  };

  const computeStatus = (item) => {
    return Number(item?.status) === 1 ? "Active" : "Inactive";
  };

  // ---- MASTER VALUE EXTRACTOR (used for sorting + export) ----
  // Every column that should be sortable MUST have a case here matching its sortKey.
  const getValue = (item, key) => {
    switch (key) {
      // Nested profile fields
      case "first_name":
        return item?.profile?.first_name || "";
      case "profile_type":
        return item?.profile?.profile_type || "";
      case "location":
        return item?.profile?.location || "";
      case "speciality":
        return (item?.profile?.speciality_names || []).join(", ");
      case "source":
        return formatSourceLabel(item?.profile?.signup_source) || "";

      // Direct fields
      case "mobile":
        return item?.mobile || "";
      case "email":
        return item?.email || "";
      case "status":
        return computeStatus(item);
      case "user_type":
        return item?.user_type ?? "";
      case "last_login":
        return item?.last_login || "";
      case "last_visit":
        return item?.last_visit || "";
      case "created_date":
        return item?.created_date || "";
      case "last_activity":
        return item?.last_activity || "";
      case "popup_url":
        return item?.popup_url || "";
      case "recent_action":
        return item?.recent_action?.page_url || "";

      // Nested object → product name / counts
      case "catalog":
        return item?.latest_catalog?.product_name || "";
      case "demo":
        return item?.latest_demo_url?.product_name || "";
      case "need_help":
        return Object.values(item?.need_help_stats || {}).reduce(
          (s, v) => s + v,
          0
        );
      case "sm_outbound":
        return Object.values(item?.social_media_outbound_stats || {}).reduce(
          (s, v) => s + v,
          0
        );

      // Numeric counts
      case "cart":
        return item?.cart_products_count || 0;
      case "checkout":
        return item?.checkout_products_count || 0;
      case "recently_viewed_product_count":
        return item?.recently_viewed_product_count || 0;
      case "wishlist_count":
        return item?.wishlist_count || 0;
      case "communication_history_count":
        return (item?.communication_history || []).length;
      case "total_products_purchased":
        return item?.total_products_purchased || 0;

      default:
        return item?.[key] ?? "";
    }
  };

  // ---- Filter handlers ----
  const toggleSource = (value) => {
    setSourceFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    resetPage();
  };

  const toggleCustomerType = (value) => {
    setCustomerTypeFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    resetPage();
  };

  const toggleLastActivity = (value) => {
    setLastActivityFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    resetPage();
  };

  const clearFilters = () => {
    setSourceFilter([]);
    setCustomerTypeFilter([]);
    setUserTypeFilter("");
    setLastActivityFilter([]);
    setDateFrom("");
    setDateTo("");
    setDateFilterType("created_date");
    setStatusFilter("all");
    setMobileFilter("");
    setCustomerNameFilter("");
    setEmailFilter("");
    setCartMin("");
    setCartMax("");
    setQtyMin("");
    setQtyMax("");
    setShowFilter({
      mobile: false,
      customerName: false,
      email: false,
      cart: false,
      qty: false,
    });
    resetPage();
  };

  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    resetPage();
  };
  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    resetPage();
  };
  const handleDateFilterTypeChange = (e) => {
    setDateFilterType(e.target.value);
    resetPage();
  };
  const handleStatusSelect = (value) => {
    setStatusFilter(value);
    resetPage();
    setShowStatusDropdown(false);
  };
  const handleUserTypeSelect = (value) => {
    setUserTypeFilter(value);
    resetPage();
    setShowUserTypeDropdown(false);
  };
  const handleMobileChange = (e) => {
    setMobileFilter(e.target.value);
    resetPage();
  };
  const handleCustomerNameChange = (e) => {
    setCustomerNameFilter(e.target.value);
    resetPage();
  };
  const handleEmailChange = (e) => {
    setEmailFilter(e.target.value);
    resetPage();
  };
  const handleCartMinChange = (e) => {
    setCartMin(e.target.value);
    resetPage();
  };
  const handleCartMaxChange = (e) => {
    setCartMax(e.target.value);
    resetPage();
  };
  const handleQtyMinChange = (e) => {
    setQtyMin(e.target.value);
    resetPage();
  };
  const handleQtyMaxChange = (e) => {
    setQtyMax(e.target.value);
    resetPage();
  };
  const handleLastActivitySelect = (value) => {
    setLastActivityFilter([value]);
    resetPage();
    setShowLastActivityDropdown(false);
  };

  // ---- Drag-and-drop reorder ----
  const draggedColRef = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const moveColumn = (fromLabel, toLabel) => {
    setVisibleColumns((prev) => {
      const fromIdx = prev.indexOf(fromLabel);
      const toIdx = prev.indexOf(toLabel);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromLabel);
      return next;
    });
  };

  // ---- Column visibility toggle ----
  const toggleColumn = (label) => {
    setVisibleColumns((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const toggleFilterInput = (key) => {
    setShowFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ---- Sorting ----
  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  // ---- Client-side filtering + sorting ----
  const filteredData = useMemo(() => {
    let data = [...userlistData];

    if (sourceFilter.length > 0) {
      data = data.filter((item) =>
        sourceFilter.includes(item?.profile?.signup_source?.toLowerCase())
      );
    }

    if (customerTypeFilter.length > 0) {
      data = data.filter((item) =>
        customerTypeFilter.includes(item?.profile?.profile_type?.toLowerCase())
      );
    }

    if (userTypeFilter) {
      const term = userTypeFilter.toLowerCase();
      data = data.filter(
        (item) => (item?.user_type || "").toString().toLowerCase() === term
      );
    }

    if (lastActivityFilter.length > 0) {
      data = data.filter((item) =>
        lastActivityFilter.includes(item?.last_activity || "")
      );
    }

    if (customerNameFilter) {
      const term = customerNameFilter.toLowerCase();
      data = data.filter((item) =>
        (item?.profile?.first_name || "").toLowerCase().includes(term)
      );
    }

    if (emailFilter) {
      const term = emailFilter.toLowerCase();
      data = data.filter((item) =>
        (item?.email || "").toLowerCase().includes(term)
      );
    }

    if (mobileFilter) {
      const term = mobileFilter.trim().toLowerCase();
      data = data.filter((item) =>
        String(item?.mobile || "")
          .toLowerCase()
          .includes(term)
      );
    }

    if (cartMin) {
      const min = parseInt(cartMin, 10);
      data = data.filter((item) => (item?.cart_products_count || 0) >= min);
    }
    if (cartMax) {
      const max = parseInt(cartMax, 10);
      data = data.filter((item) => (item?.cart_products_count || 0) <= max);
    }

    if (qtyMin) {
      const min = parseInt(qtyMin, 10);
      data = data.filter(
        (item) => (item?.total_products_purchased || 0) >= min
      );
    }
    if (qtyMax) {
      const max = parseInt(qtyMax, 10);
      data = data.filter(
        (item) => (item?.total_products_purchased || 0) <= max
      );
    }

    // ---- SORTING (auto-detect numbers, dates, strings) ----
    if (sortConfig.key) {
      data.sort((a, b) => {
        const rawA = getValue(a, sortConfig.key);
        const rawB = getValue(b, sortConfig.key);

        // 1) Numeric
        if (typeof rawA === "number" && typeof rawB === "number") {
          return sortConfig.direction === "asc" ? rawA - rawB : rawB - rawA;
        }

        // 2) Date (only if BOTH look like dates)
        const dateA = Date.parse(rawA);
        const dateB = Date.parse(rawB);
        const looksLikeDate =
          typeof rawA === "string" &&
          typeof rawB === "string" &&
          rawA.length > 6 &&
          rawB.length > 6 &&
          !isNaN(dateA) &&
          !isNaN(dateB);

        if (looksLikeDate) {
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        // 3) Case-insensitive string
        const strA = String(rawA).toLowerCase();
        const strB = String(rawB).toLowerCase();
        if (strA < strB) return sortConfig.direction === "asc" ? -1 : 1;
        if (strA > strB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [
    userlistData,
    sourceFilter,
    customerTypeFilter,
    userTypeFilter,
    dateFrom,
    dateTo,
    dateFilterType,
    statusFilter,
    lastActivityFilter,
    customerNameFilter,
    mobileFilter,
    emailFilter,
    cartMin,
    cartMax,
    qtyMin,
    qtyMax,
    sortConfig,
  ]);

  // ---- Options for dropdowns ----
  const sourceOptions = useMemo(() => {
    const values = new Set();
    userlistData.forEach((item) => {
      const raw = item?.profile?.signup_source;
      if (raw) values.add(raw);
    });
    return [...values]
      .map((value) => ({ value, label: formatSourceLabel(value) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [userlistData]);

  const customerTypeOptions = [
    "doctor",
    "hospital",
    "manufacturer",
    "distributor",
  ];

  const lastActivityOptions = useMemo(() => {
    const values = new Set();
    userlistData.forEach((item) => {
      const raw = item?.last_activity;
      if (raw) values.add(raw);
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [userlistData]);

  const userTypeOptions = useMemo(() => {
    const values = new Set();
    userlistData.forEach((item) => {
      const raw = item?.user_type;
      if (raw !== undefined && raw !== null) values.add(raw.toString());
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [userlistData]);

  // ---- Dynamic header labels ----
  const sourceHeaderLabel = useMemo(() => {
    if (sourceFilter.length === 0) return "Source";
    if (sourceFilter.length === 1) {
      const found = sourceOptions.find((opt) => opt.value === sourceFilter[0]);
      return found ? `Source: ${found.label}` : "Source";
    }
    return `Source (${sourceFilter.length})`;
  }, [sourceFilter, sourceOptions]);

  const customerTypeHeaderLabel = useMemo(() => {
    if (customerTypeFilter.length === 0) return "Customer Type";
    if (customerTypeFilter.length === 1) {
      const found = customerTypeFilter[0];
      return found ? `Type: ${found}` : "Customer Type";
    }
    return `Type (${customerTypeFilter.length})`;
  }, [customerTypeFilter]);

  const lastActivityHeaderLabel = useMemo(() => {
    if (lastActivityFilter.length === 0) return "Purpose";
    if (lastActivityFilter.length === 1) {
      return `Purpose: ${lastActivityFilter[0]}`;
    }
    return `Purpose (${lastActivityFilter.length})`;
  }, [lastActivityFilter]);

  const userTypeHeaderLabel = useMemo(() => {
    if (!userTypeFilter) return "User Type";
    return `User Type: ${userTypeFilter}`;
  }, [userTypeFilter]);

  // ---- Click-outside for dropdowns ----
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sourceDropdownRef.current &&
        !sourceDropdownRef.current.contains(event.target)
      ) {
        setShowSourceDropdown(false);
      }
      if (
        customerTypeDropdownRef.current &&
        !customerTypeDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerTypeDropdown(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        lastActivityDropdownRef.current &&
        !lastActivityDropdownRef.current.contains(event.target)
      ) {
        setShowLastActivityDropdown(false);
      }
      if (
        userTypeDropdownRef.current &&
        !userTypeDropdownRef.current.contains(event.target)
      ) {
        setShowUserTypeDropdown(false);
      }
      if (
        columnSelectorRef.current &&
        !columnSelectorRef.current.contains(event.target)
      ) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---- Data fetch ----
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Guard: don't fetch until text filters have settled
    if (mobileFilter !== debouncedMobile) return;
    if (customerNameFilter !== debouncedName) return;
    if (emailFilter !== debouncedEmail) return;

    // Cancel any previous in-flight request
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    (async () => {
      setLoader(true);

      const config = {
        _page: currPage,
        _limit: perPage,
      };

      if (customerTypeFilter.length) config.profile_type = customerTypeFilter.join(",");
      if (userTypeFilter) config.usertype = userTypeFilter;
      if (debouncedMobile) config.mobile = debouncedMobile;
      if (debouncedName) config.first_name = debouncedName;
      if (sourceFilter.length) config.source = sourceFilter.join(",");
      if (dateFrom) config.from_date = dateFrom;
      if (dateTo) config.to_date = dateTo;
      if (dateFrom || dateTo) config.date_filter = dateFilterType;

      // ⬇⬇ FIX: send status as STRING so postData1 doesn't drop the falsy 0
      if (statusFilter !== "all") config.status = String(statusFilter);

      if (debouncedEmail) config.email = debouncedEmail;
      if (cartMin) config.cart_min = cartMin;
      if (cartMax) config.cart_max = cartMax;
      if (qtyMin) config.qty_min = qtyMin;
      if (qtyMax) config.qty_max = qtyMax;

      // ⬇⬇ NEW: send sort info to the server so sorting works across pages
      if (sortConfig.key) {
        config.sort_key = sortConfig.key;
        config.sort_dir = sortConfig.direction;      // "asc" | "desc"
      }

      try {
        const res = await postData1("admin/user_list/", config, true, true);
        if (controller.signal.aborted) return;

        const users = res?.data?.data || [];
        setUserListData(users);

        setPaging({
          total: res?.data?.total,
          total_pages: res?.data?.total_pages,
        });
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Failed to fetch users:", error);
        }
      } finally {
        if (!controller.signal.aborted) setLoader(false);
      }
    })();

    return () => controller.abort();
  }, [
    currPage,
    perPage,
    sourceFilter,
    customerTypeFilter,
    userTypeFilter,
    dateFrom,
    dateTo,
    dateFilterType,
    statusFilter,
    lastActivityFilter,
    mobileFilter,
    customerNameFilter,
    emailFilter,
    debouncedMobile,
    debouncedName,
    debouncedEmail,
    cartMin,
    cartMax,
    qtyMin,
    qtyMax,
    // ⬇⬇ NEW: re-fetch when the user changes sort
    sortConfig.key,
    sortConfig.direction,
  ]);

  // ---- Sort Icon ----
  const SortIcon = ({ column }) => {
    const isActive = sortConfig.key === column;
    const isAsc = isActive && sortConfig.direction === "asc";
    const isDesc = isActive && sortConfig.direction === "desc";
    return (
      <span
        className="inline-flex flex-col ml-1 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          handleSort(column);
        }}
        title={
          isAsc
            ? "Sorted ascending"
            : isDesc
              ? "Sorted descending"
              : "Click to sort"
        }
      >
        <svg
          className={`w-2.5 h-2.5 -mb-0.5 ${isAsc ? "text-teal-600" : "text-gray-400"
            }`}
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8 4l4 6H4z" />
        </svg>
        <svg
          className={`w-2.5 h-2.5 ${isDesc ? "text-teal-600" : "text-gray-400"}`}
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8 12L4 6h8z" />
        </svg>
      </span>
    );
  };

  const truncate = (str, n) =>
    str && str.length > n ? `${str.substring(0, n)}…` : str;

  // ---- Column definitions (EVERY column has a sortKey now) ----
  const columns = [
    { label: "S.No", sortKey: null, width: "w-14", noFilter: true },
    { label: "Source", sortKey: "source", width: "w-28", isFilter: "source" },
    {
      label: "Account Created",
      sortKey: "created_date",
      width: "w-32",
    },
    {
      label: "User ID (Mobile No.)",
      sortKey: "mobile",
      width: "w-44",
      isFilter: "text",
      filterKey: "mobile",
    },
    {
      label: "Customer Name",
      sortKey: "first_name",
      width: "w-40",
      isFilter: "text",
      filterKey: "customerName",
    },
    {
      label: "Email ID",
      sortKey: "email",
      width: "w-56",
      isFilter: "text",
      filterKey: "email",
    },
    {
      label: "Customer Type",
      sortKey: "profile_type",
      width: "w-36",
      isFilter: "customerType",
    },
    {
      label: "Purpose",
      sortKey: "last_activity",
      width: "w-24",
      isFilter: "lastActivity",
    },
    {
      label: "Last Visited",
      sortKey: "last_visit",
      width: "w-36",
      noFilter: true,
    },
    {
      label: "Speciality",
      sortKey: "speciality",
      width: "w-20",
      noFilter: true,
    },
    { label: "Location", sortKey: "location", width: "w-32" },
    {
      label: "Last Login",
      sortKey: "last_login",
      width: "w-28",
      noFilter: true,
    },
    {
      label: "Recent Action",
      sortKey: "recent_action",
      width: "w-24",
      noFilter: true,
    },
    {
      label: "Pop up URL",
      sortKey: "popup_url",
      width: "w-44",
      noFilter: true,
    },
    {
      label: "Need Help",
      sortKey: "need_help",
      width: "w-28",
      noFilter: true,
    },
    {
      label: "Brochure Download",
      sortKey: "catalog",
      width: "w-44",
      noFilter: true,
    },
    {
      label: "Arrange Demo",
      sortKey: "demo",
      width: "w-36",
      noFilter: true,
    },
    {
      label: "SM Outbound",
      sortKey: "sm_outbound",
      width: "w-32",
      noFilter: true,
    },
    {
      label: "Add to Cart",
      sortKey: "cart",
      width: "w-40",
      isFilter: "cart",
      filterKey: "cart",
    },
    {
      label: "Checkout Cart",
      sortKey: "checkout",
      width: "w-40",
      noFilter: true,
    },
    {
      label: "Recently Viewed Products",
      sortKey: "recently_viewed_product_count",
      width: "w-48",
      noFilter: true,
    },
    {
      label: "Wishlist",
      sortKey: "wishlist_count",
      width: "w-28",
      noFilter: true,
    },
    {
      label: "Communication History",
      sortKey: "communication_history_count",
      width: "w-44",
      noFilter: true,
    },
    {
      label: "Qty",
      sortKey: "total_products_purchased",
      width: "w-16",
      isFilter: "qty",
      filterKey: "qty",
    },
    {
      label: "Status",
      sortKey: "status",
      width: "w-32",
      isFilter: "status",
    },
    { label: "Action", sortKey: null, width: "w-28", noFilter: true },
  ];

  const filtersActive =
    sourceFilter.length > 0 ||
    customerTypeFilter.length > 0 ||
    userTypeFilter ||
    lastActivityFilter.length > 0 ||
    dateFrom ||
    dateTo ||
    statusFilter !== "all" ||
    mobileFilter ||
    customerNameFilter ||
    emailFilter ||
    cartMin ||
    cartMax ||
    qtyMin ||
    qtyMax;

  // ---- Column Renderers ----
  const columnRenderers = {
    "S.No": (data, index) => (
      <td
        key="S.No"
        className="px-4 py-3.5 text-sm font-medium text-gray-400 border-r border-gray-50 w-14 text-center"
      >
        {(currPage - 1) * perPage + index + 1}
      </td>
    ),
    Source: (data, index) => (
      <td
        key="Source"
        className="px-4 py-3.5 border-r border-gray-50 w-28 text-center"
      >
        <span className="inline-block min-w-[90px] px-3 py-1 rounded-full text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-600 shadow-sm">
          {formatSourceLabel(data?.profile?.signup_source)
            .charAt(0)
            .toUpperCase() +
            formatSourceLabel(data?.profile?.signup_source).slice(1)}
        </span>
      </td>
    ),
    "Account Created": (data, index) => (
      <td
        key="Account Created"
        className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-32 whitespace-nowrap"
      >
        {data?.created_date
          ? new Date(data.created_date).toLocaleDateString("en-IN")
          : "—"}
      </td>
    ),
    "User ID (Mobile No.)": (data, index) => (
      <td
        onClick={(e) => e.stopPropagation()}
        key="User ID (Mobile No.)"
        className="px-4 py-3.5 text-sm text-gray-700 font-mono border-r border-gray-50 w-44 whitespace-nowrap"
      >
        {data?.mobile || "—"}
      </td>
    ),
    "Customer Name": (data, index) => (
      <td
        onClick={(e) => e.stopPropagation()}
        key="Customer Name"
        className="px-4 py-3.5 border-r border-gray-50 w-40"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {data?.profile?.first_name?.[0]?.toUpperCase() || "?"}
          </div>
          <span
            className="text-sm font-medium text-gray-800 truncate"
            title={data?.profile?.first_name}
          >
            {data?.profile?.first_name || "—"}
          </span>
        </div>
      </td>
    ),
    "Email ID": (data, index) => (
      <td
        onClick={(e) => e.stopPropagation()}
        key="Email ID"
        className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-56"
      >
        <span title={data?.email} className="truncate block max-w-[200px]">
          {truncate(data?.email, 28) || "—"}
        </span>
      </td>
    ),
    "Customer Type": (data, index) => (
      <td
        key="Customer Type"
        className="px-4 py-3.5 border-r border-gray-50 w-36 text-center"
      >
        {data?.profile?.profile_type ? (
          <span
            className={`inline-block min-w-[100px] px-3 py-1 rounded-full text-sm font-medium border capitalize ${data.profile.profile_type === "doctor"
              ? "bg-blue-100 text-blue-700 border-blue-500"
              : data.profile.profile_type === "hospital"
                ? "bg-purple-100 text-purple-700 border-purple-500"
                : data.profile.profile_type === "manufacturer"
                  ? "bg-orange-100 text-orange-700 border-orange-500"
                  : "bg-teal-100 text-teal-700 border-teal-500"
              }`}
          >
            {data.profile.profile_type}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        )}
      </td>
    ),
    Purpose: (data, index) => (
      <td
        key="Purpose"
        className="px-4 py-3.5 border-r border-gray-50 w-32 text-sm text-gray-700"
      >
        {data?.last_activity ? (
          <span
            className="inline-block max-w-[180px] truncate"
            title={data.last_activity}
          >
            {data.last_activity}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    ),
    "Last Visited": (data, index) => (
      <td
        key="Last Visited"
        className="px-4 py-3.5 text-sm text-gray-500 border-r border-gray-50 whitespace-nowrap"
      >
        {data?.last_visit
          ? new Date(data.last_visit).toLocaleString("en-IN", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          : "—"}
      </td>
    ),
    Speciality: (data, index) => (
      <td key="Speciality" className="px-4 py-3.5 border-r border-gray-50 w-52">
        <div className="flex flex-wrap gap-1">
          {data?.profile?.speciality_names?.length > 0 ? (
            data.profile.speciality_names.map((item, idx) => (
              <span
                key={idx}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-700 border-blue-500"
              >
                {item}
              </span>
            ))
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      </td>
    ),
    Location: (data, index) => (
      <td
        key="Location"
        className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-52"
      >
        <span
          title={data?.profile?.location}
          className="block truncate max-w-[250px]"
        >
          {truncate(data?.profile?.location, 38) || "—"}
        </span>
      </td>
    ),
    "Last Login": (data, index) => (
      <td
        key="Last Login"
        className="px-4 py-3.5 text-sm text-gray-500 border-r border-gray-50 whitespace-nowrap"
      >
        {data?.last_login
          ? new Date(data.last_login).toLocaleString("en-IN", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          : "—"}
      </td>
    ),
    "Recent Action": (data, index) => (
      <td
        key="Recent Action"
        className="px-4 py-3.5 border-r border-gray-50 w-44"
      >
        {data?.recent_action?.page_url ? (
          <a
            href={`${process.env.REACT_APP_FRONTEND_URL}${data.recent_action.page_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-teal-600 hover:text-teal-800 hover:underline block truncate max-w-[180px]"
            title={data.recent_action.page_url}
          >
            View Page
          </a>
        ) : (
          "—"
        )}
      </td>
    ),
    "Pop up URL": (data, index) => (
      <td
        key="Pop up URL"
        className="px-4 py-3.5 border-r border-gray-50 w-44"
      >
        {data?.popup_url ? (
          <a
            href={data.popup_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-teal-600 hover:text-teal-800 hover:underline truncate block max-w-[160px]"
            title={data.popup_url}
          >
            Latest Popup URL
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    ),
    "Need Help": (data, index) => (
      <td key="Need Help" className="px-4 py-3.5 border-r border-gray-50 w-32">
        <div className="relative group inline-block">
          <span className="inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border bg-violet-100 text-violet-700 border-violet-500 cursor-pointer text-center">
            {Object.values(data?.need_help_stats || {}).reduce(
              (sum, val) => sum + val,
              0
            )}{" "}
            Clicks
          </span>
          <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[220px]">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-2">
                Need Help Actions
              </h4>
              {Object.entries(data?.need_help_stats || {}).map(
                ([action, count]) => (
                  <div
                    key={action}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="capitalize text-sm text-gray-600">
                      {action.replaceAll("_", " ")}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">
                      {count}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </td>
    ),
    "Brochure Download": (data, index) => (
      <td
        key="Brochure Download"
        className="px-4 py-3.5 border-r border-gray-50 w-44"
      >
        {data?.latest_catalog ? (
          <a
            href={`${process.env.REACT_APP_FRONTEND_URL}/${data.latest_catalog.product_slug}`}
            className="block text-xs text-teal-600 hover:text-teal-800 hover:underline truncate max-w-[160px]"
            title={data.latest_catalog.product_name}
            target="_blank"
          >
            {data.latest_catalog.product_name}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    ),
    "Arrange Demo": (data, index) => (
      <td
        key="Arrange Demo"
        className="px-4 py-3.5 border-r border-gray-50 w-44"
      >
        {data?.latest_demo_url ? (
          <a
            href={`${process.env.REACT_APP_FRONTEND_URL}/${data.latest_demo_url.product_slug}`}
            className="block text-xs text-teal-600 hover:text-teal-800 hover:underline truncate max-w-[160px]"
            title={data.latest_demo_url.product_name}
            target="_blank"
          >
            {data.latest_demo_url.product_name}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    ),
    "SM Outbound": (data, index) => (
      <td
        key="SM Outbound"
        className="px-4 py-3.5 border-r border-gray-50 w-32"
      >
        <div className="relative group inline-block">
          <span className="inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border bg-cyan-100 text-cyan-700 border-cyan-500 cursor-pointer text-center">
            {Object.values(data?.social_media_outbound_stats || {}).reduce(
              (sum, val) => sum + val,
              0
            )}{" "}
            Clicks
          </span>
          <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[220px]">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-2">
                Social Media Outbound
              </h4>
              {Object.entries(data?.social_media_outbound_stats || {}).map(
                ([platform, count]) => (
                  <div
                    key={platform}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="capitalize text-sm text-gray-600">
                      {platform}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-xs font-medium">
                      {count}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </td>
    ),
    "Add to Cart": (data, index) => (
      <td
        key="Add to Cart"
        className="px-4 py-3.5 border-r border-gray-50 w-40"
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUserUid(data.uid);
            setModalType("cart");
            setModalTitle("Add To Cart Products");
            setShowProductsModal(true);
          }}
          className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 font-medium transition-colors"
        >
          {data?.cart_products_count || 0} Products
        </button>
      </td>
    ),
    "Checkout Cart": (data, index) => (
      <td
        key="Checkout Cart"
        className="px-4 py-3.5 border-r border-gray-50 w-40"
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUserUid(data.uid);
            setModalType("checkout");
            setModalTitle("Checkout Products");
            setShowProductsModal(true);
          }}
          className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-medium transition-colors"
        >
          {data?.checkout_products_count || 0} Products
        </button>
      </td>
    ),
    "Recently Viewed Products": (data, index) => (
      <td
        key="Recently Viewed Products"
        className="px-4 py-3.5 border-r border-gray-50 w-48 text-center"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setActivityModal({ uid: data.uid, type: "visited" });
          }}
          className="text-teal-700 hover:text-teal-900 hover:underline"
          title="View recently viewed products"
        >
          {data?.recently_viewed_product_count || 0}
        </button>
      </td>
    ),
    Wishlist: (data, index) => (
      <td
        key="Wishlist"
        className="px-4 py-3.5 border-r border-gray-50 w-28 text-center"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setActivityModal({ uid: data.uid, type: "wishlist" });
          }}
          className="text-teal-700 hover:text-teal-900 hover:underline"
          title="View wishlist products"
        >
          {data?.wishlist_count || 0}
        </button>
      </td>
    ),
    "Communication History": (data) => (
      <td
        key="Communication History"
        className="px-4 py-3.5 border-r border-gray-50 w-44 text-center"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setCommunicationModal({
              customerName:
                data?.profile?.first_name || data?.email || data?.mobile || "User",
              messages: data?.communication_history || [],
            });
          }}
          className="text-teal-700 hover:text-teal-900 hover:underline"
          title="Open communication history"
        >
          {(data?.communication_history || []).length} messages
        </button>
      </td>
    ),
    Qty: (data, index) => (
      <td
        key="Qty"
        className="px-4 py-3.5 border-r border-gray-50 w-20 text-center"
      >
        <span
          className={`inline-flex items-center justify-center min-w-[42px] px-3 py-1 rounded-full text-sm font-bold text-white shadow-sm ${(data?.total_products_purchased || 0) >= 10
            ? "bg-green-600"
            : (data?.total_products_purchased || 0) >= 5
              ? "bg-blue-600"
              : (data?.total_products_purchased || 0) > 0
                ? "bg-violet-600"
                : "bg-gray-400"
            }`}
        >
          {data?.total_products_purchased || 0}
        </span>
      </td>
    ),
    Status: (data, index) => {
      const isActive = Number(data?.status) === 1;
      return (
        <td
          key="Status"
          className="px-4 py-3.5 border-r border-gray-50 w-32 text-center"
        >
          <span
            className={`inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border transition-all ${isActive
              ? "bg-teal-100 text-teal-700 border-teal-500"
              : "bg-red-100 text-red-500 border-red-500"
              }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </td>
      );
    },
    Action: (data, index) => (
      <td key="Action" className="px-4 py-3.5 w-28">
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/manageusers/viewuser/${data.uid}`);
            }}
            className="p-2 rounded-lg hover:bg-teal-50 text-gray-400 hover:text-teal-600 transition-colors group"
            title="View user"
          >
            <svg
              className="w-5 h-5 group-hover:scale-110 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </button>
        </div>
      </td>
    ),
  };

  // ---- Render ----
  return (
    <section className="p-6 bg-white min-h-screen">
      <div className="mb-6">
        <div className="flex flex-row items-center justify-between gap-4">
          <div className="flex flex- items-center gap-4">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-800 tracking-tight">
                User Management
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Manage and monitor all registered users
              </p>
            </div>
          </div>

          <div className="flex flex-row items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setIsUserRegOpen(true);
                }}
                className="text-xs text-gray-500 font-medium px-2 py-2 rounded-md bg-[#019c81] text-white"
              >
                Register User
              </button>
            </div>
            <div className="flex items-center gap-1">
              <select
                value={dateFilterType}
                onChange={handleDateFilterTypeChange}
                className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none"
                title="Select which date to filter on"
              >
                <option value="created_date">Account Created</option>
                <option value="last_login">Last Login</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-gray-500 font-medium">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={handleDateFromChange}
                className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none w-36"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-gray-500 font-medium">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={handleDateToChange}
                className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none w-36"
              />
            </div>

            {filtersActive && (
              <button
                onClick={clearFilters}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors shadow-sm"
              >
                Clear Filters
              </button>
            )}

            <div className="relative" ref={columnSelectorRef}>
              <button
                onClick={() => setShowColumnSelector((prev) => !prev)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors shadow-sm"
                title="Toggle columns"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7"
                  />
                </svg>
                Columns
                <span className="ml-0.5 bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
                  {visibleColumns.length}/{ALL_COLUMNS.length}
                </span>
              </button>

              {showColumnSelector && (
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-56 rounded-2xl border border-gray-200 bg-white shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-4 py-2.5 text-xs font-semibold text-gray-500 border-b border-gray-100">
                    Show / Hide Columns
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {[
                      ...visibleColumns,
                      ...ALL_COLUMNS.filter(
                        (c) => !visibleColumns.includes(c.label)
                      ).map((c) => c.label),
                    ].map((colLabel) => {
                      const col = ALL_COLUMNS.find((c) => c.label === colLabel);
                      if (!col) return null;
                      const checked = visibleColumns.includes(col.label);
                      const isDragOver = dragOverCol === col.label;
                      return (
                        <div
                          key={col.label}
                          draggable={checked}
                          onDragStart={(e) => {
                            draggedColRef.current = col.label;
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setDragOverCol(col.label);
                          }}
                          onDragLeave={() => {
                            if (dragOverCol === col.label) setDragOverCol(null);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = draggedColRef.current;
                            const to = col.label;
                            if (from && to && from !== to) {
                              moveColumn(from, to);
                            }
                            draggedColRef.current = null;
                            setDragOverCol(null);
                          }}
                          onDragEnd={() => {
                            draggedColRef.current = null;
                            setDragOverCol(null);
                          }}
                          className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors ${checked ? "text-teal-700 font-medium" : "text-gray-500"
                            } ${isDragOver
                              ? "bg-teal-50 border-t-2 border-teal-400"
                              : "border-t border-transparent"
                            } ${checked
                              ? "cursor-grab active:cursor-grabbing hover:bg-gray-50"
                              : ""
                            }`}
                          onClick={() => {
                            if (checked && draggedColRef.current) return;
                            toggleColumn(col.label);
                          }}
                        >
                          {checked && (
                            <svg
                              className="w-3.5 h-3.5 mr-1 text-gray-400 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 2a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 8a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
                            </svg>
                          )}
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => { }}
                            className="mr-2.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
                          />
                          <span className="truncate">{col.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg w-10 h-10 text-white cursor-pointer transition-colors"></div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh - 300px)]">
          <table
            className="border-collapse  w-full"
            style={{
              minWidth: visibleColumns.length < 12 ? "auto" : "2800px",
              width: visibleColumns.length < 12 ? "100%" : "max-content",
            }}
          >
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                {visibleColumns.map((colLabel) => {
                  const col = columns.find((c) => c.label === colLabel);
                  if (!col) return null;
                  const { label, sortKey, width, isFilter, noFilter, filterKey } =
                    col;
                  const isSource = isFilter === "source";
                  const isCustomerType = isFilter === "customerType";
                  const isStatus = isFilter === "status";
                  const isLastActivity = isFilter === "lastActivity";
                  const isUserType = isFilter === "userType";
                  const isText = isFilter === "text";
                  const isCart = isFilter === "cart";
                  const isQty = isFilter === "qty";
                  const hasFilter =
                    isSource ||
                    isCustomerType ||
                    isUserType ||
                    isStatus ||
                    isLastActivity ||
                    isText ||
                    isCart ||
                    isQty;
                  const showFilterInput = filterKey
                    ? showFilter[filterKey]
                    : false;

                  let displayLabel = label;
                  if (isSource) displayLabel = sourceHeaderLabel;
                  if (isCustomerType) displayLabel = customerTypeHeaderLabel;
                  if (isUserType) displayLabel = userTypeHeaderLabel;
                  if (isLastActivity) displayLabel = lastActivityHeaderLabel;
                  if (isStatus) {
                    displayLabel =
                      statusFilter === "all"
                        ? "Status"
                        : statusFilter === 1
                          ? "Status: Active"
                          : "Status: Inactive";
                  }

                  const handleHeaderClick = () => {
                    if (isText || isCart || isQty) {
                      toggleFilterInput(filterKey);
                    } else if (isSource) {
                      setShowSourceDropdown((prev) => !prev);
                    } else if (isCustomerType) {
                      setShowCustomerTypeDropdown((prev) => !prev);
                    } else if (isUserType) {
                      setShowUserTypeDropdown((prev) => !prev);
                    } else if (isLastActivity) {
                      setShowLastActivityDropdown((prev) => !prev);
                    } else if (isStatus) {
                      setShowStatusDropdown((prev) => !prev);
                    } else if (sortKey && !hasFilter) {
                      handleSort(sortKey);
                    }
                  };

                  return (
                    <th
                      key={label}
                      ref={
                        isSource
                          ? sourceDropdownRef
                          : isCustomerType
                            ? customerTypeDropdownRef
                            : isLastActivity
                              ? lastActivityDropdownRef
                              : isUserType
                                ? userTypeDropdownRef
                                : isStatus
                                  ? statusDropdownRef
                                  : null
                      }
                      onClick={handleHeaderClick}
                      className={`
                        group sticky top-0 z-20 bg-gradient-to-b from-gray-50 to-gray-100
                        px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider
                        border-r border-gray-100 last:border-r-0 select-none relative align-top
                        ${width}
                        ${(hasFilter || sortKey) && !noFilter
                          ? "cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors"
                          : "cursor-default"
                        }
                      `}
                    >
                      <div className="flex items-center gap-1 whitespace-nowrap">
                        {isText && showFilterInput ? (
                          <input
                            type="text"
                            placeholder={`Filter ${label}`}
                            value={
                              filterKey === "mobile"
                                ? mobileFilter
                                : filterKey === "customerName"
                                  ? customerNameFilter
                                  : emailFilter
                            }
                            onChange={(e) => {
                              if (filterKey === "mobile")
                                return handleMobileChange(e);
                              if (filterKey === "customerName")
                                return handleCustomerNameChange(e);
                              return handleEmailChange(e);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                          />
                        ) : isCart && showFilterInput ? (
                          <div
                            className="flex gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="number"
                              placeholder="Min"
                              value={cartMin}
                              onChange={handleCartMinChange}
                              className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={cartMax}
                              onChange={handleCartMaxChange}
                              className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                            />
                          </div>
                        ) : isQty && showFilterInput ? (
                          <div
                            className="flex gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="number"
                              placeholder="Min"
                              value={qtyMin}
                              onChange={handleQtyMinChange}
                              className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={qtyMax}
                              onChange={handleQtyMaxChange}
                              className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                            />
                          </div>
                        ) : (
                          <span>{displayLabel}</span>
                        )}

                        {/* Sort icon — visible on every sortable column */}
                        {sortKey && <SortIcon column={sortKey} />}

                        {hasFilter && !isText && !isCart && !isQty && (
                          <svg
                            className="w-3 h-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        )}

                        {isSource && sourceFilter.length > 0 && (
                          <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {sourceFilter.length}
                          </span>
                        )}
                        {isCustomerType && customerTypeFilter.length > 0 && (
                          <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {customerTypeFilter.length}
                          </span>
                        )}
                        {isLastActivity && lastActivityFilter.length > 0 && (
                          <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {lastActivityFilter.length}
                          </span>
                        )}
                      </div>

                      {/* Dropdowns */}
                      {isStatus && showStatusDropdown && (
                        <div
                          className="absolute left-0 top-full z-50 mt-1 w-40 rounded-2xl border border-gray-200 bg-white shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleStatusSelect("all")}
                            className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === "all"
                              ? "bg-teal-50 text-teal-700"
                              : "text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStatusSelect(1)}
                            className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === 1
                              ? "bg-teal-50 text-teal-700"
                              : "text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            Active
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStatusSelect(0)}
                            className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === 0
                              ? "bg-teal-50 text-teal-700"
                              : "text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            Inactive
                          </button>
                        </div>
                      )}

                      {isLastActivity && showLastActivityDropdown && (
                        <div
                          className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
                            Select Purpose
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {lastActivityOptions.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-gray-500">
                                No purposes
                              </div>
                            ) : (
                              lastActivityOptions.map((option) => {
                                const checked =
                                  lastActivityFilter.includes(option);
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() =>
                                      handleLastActivitySelect(option)
                                    }
                                    className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked
                                      ? "bg-teal-50 text-teal-700"
                                      : "text-gray-700"
                                      }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => { }}
                                      className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                    />
                                    {option}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setShowLastActivityDropdown(false)}
                              className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}

                      {isSource && showSourceDropdown && (
                        <div
                          className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
                            Select Sources
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {sourceOptions.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-gray-500">
                                No sources
                              </div>
                            ) : (
                              sourceOptions.map((option) => {
                                const checked = sourceFilter.includes(
                                  option.value
                                );
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => toggleSource(option.value)}
                                    className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked
                                      ? "bg-teal-50 text-teal-700"
                                      : "text-gray-700"
                                      }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => { }}
                                      className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                    />
                                    {option.label}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setShowSourceDropdown(false)}
                              className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}

                      {isCustomerType && showCustomerTypeDropdown && (
                        <div
                          className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
                            Select Customer Types
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {customerTypeOptions.length === 0 ? (
                              <div className="px-4 py-2 text-sm text-gray-500">
                                No types
                              </div>
                            ) : (
                              customerTypeOptions.map((type) => {
                                const checked =
                                  customerTypeFilter.includes(type);
                                return (
                                  <button
                                    key={type}
                                    type="button"
                                    onClick={() => toggleCustomerType(type)}
                                    className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked
                                      ? "bg-teal-50 text-teal-700"
                                      : "text-gray-700"
                                      }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => { }}
                                      className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                    />
                                    {type}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setShowCustomerTypeDropdown(false)}
                              className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}

                      {isUserType && showUserTypeDropdown && (
                        <div
                          className="absolute left-0 top-full z-50 mt-1 w-40 rounded-2xl border border-gray-200 bg-white shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleUserTypeSelect("")}
                            className={`block w-full px-4 py-2 text-left text-sm ${!userTypeFilter
                              ? "bg-teal-50 text-teal-700"
                              : "text-gray-700 hover:bg-gray-50"
                              }`}
                          >
                            All
                          </button>
                          {userTypeOptions.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => handleUserTypeSelect(option)}
                              className={`block w-full px-4 py-2 text-left text-sm ${userTypeFilter === option
                                ? "bg-teal-50 text-teal-700"
                                : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {filteredData.map((data, index) => (
                <tr
                  key={index}
                  onClick={() =>
                    navigate(`/admin/manageusers/viewuser/${data.uid}`)
                  }
                  className="hover:bg-teal-50/50 transition-colors cursor-pointer duration-100 even:bg-gray-50/30"
                >
                  {visibleColumns.map((colKey) => {
                    const renderer = columnRenderers[colKey];
                    return renderer ? renderer(data, index) : null;
                  })}
                </tr>
              ))}

              {!loader && filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="px-6 py-16 text-center"
                  >
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <p className="text-sm font-medium">No users found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        total={paging?.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging?.total_pages}
        updatePage={(e) => {
          const name = e.currentTarget.name;

          if (name === "prev") {
            setCurrPage((p) => Math.max(1, p - 1));
            setPrevPage(currPage);
          } else if (name === "next") {
            setCurrPage((p) =>
              p >= (paging?.total_pages || 1) ? p : p + 1
            );
            setPrevPage(currPage);
          } else {
            // ⬇⬇ robust: prefer data-page, fall back to textContent, ignore NaN
            const raw =
              e.currentTarget.getAttribute("data-page") ??
              e.currentTarget.textContent;
            const n = parseInt(raw, 10);
            if (!Number.isNaN(n)) setCurrPage(n);
          }

          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      {loader && <Loader />}
      {activityModal && (
        <div
          className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setActivityModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-product-activity-title"
            className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 id="user-product-activity-title" className="text-lg font-semibold text-gray-800">
                {activityModal.type === "visited" ? "Recently Viewed Products" : "Wishlist Products"}
              </h2>
              <button
                type="button"
                onClick={() => setActivityModal(null)}
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            <div className="max-h-[calc(90vh-4.5rem)] overflow-y-auto p-6">
              {activityModal.type === "visited" ? (
                <ProductVisitedList userUid={activityModal.uid} />
              ) : (
                <ProductWishlistList userUid={activityModal.uid} />
              )}
            </div>
          </div>
        </div>
      )}
      {communicationModal && (
        <div
          className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setCommunicationModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="communication-history-title"
            className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2
                  id="communication-history-title"
                  className="text-lg font-semibold text-gray-800"
                >
                  Communication History
                </h2>
                <p className="text-sm text-gray-500">
                  Conversation with {communicationModal.customerName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCommunicationModal(null)}
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-6">
              {communicationModal.messages.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-500">
                  No communication history found.
                </p>
              ) : (
                communicationModal.messages.map((message) => {
                  const isAdmin = !message.sender_name;
                  const author = isAdmin ? "Admin" : message.sender_name;
                  const messageDate = message.created_date
                    ? new Date(message.created_date).toLocaleString("en-IN")
                    : "";

                  return (
                    <div
                      key={message.uid}
                      className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-3 shadow-sm ${
                          isAdmin
                            ? "bg-teal-700 text-white"
                            : "border border-gray-200 bg-white text-gray-800"
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-6">
                          <span className="text-xs font-semibold">{author}</span>
                          <time
                            className={`text-[11px] ${
                              isAdmin ? "text-teal-100" : "text-gray-400"
                            }`}
                          >
                            {messageDate}
                          </time>
                        </div>
                        {message.message_type === "text" ? (
                          <p className="whitespace-pre-wrap break-words text-sm">
                            {message.message}
                          </p>
                        ) : (
                          <a
                            href={message.file_url || message.message}
                            target="_blank"
                            rel="noreferrer"
                            className={`text-sm underline ${
                              isAdmin ? "text-white" : "text-teal-700"
                            }`}
                          >
                            Open {message.message_type || "attachment"}
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      <ProductDetailsModal
        isOpen={showProductsModal}
        onClose={() => {
          setShowProductsModal(false);
          setSelectedUserUid("");
          setModalType("");
        }}
        uid={selectedUserUid}
        type={modalType}
        title={modalTitle}
      />

      {isUserRegOpen && (
        <UserRegisterAdmin
          isOpen={isUserRegOpen}
          onClose={() => setIsUserRegOpen(false)}
        />
      )}
    </section>
  );
};

// ManufacturerList, DistributorList, BlockToggle remain unchanged

export const ManufacturerList = () => {
  const navigate = useNavigate();
  const [manlistData, setManlistData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currPage, setCurrPage] = useState(1);
  const [paging, setPaging] = useState({});
  const [prevPage, setPrevPage] = useState(1);
  const [perPage, setPerPage] = useState(8);

  const updatePage = (e) => {
    let name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage(currPage === 1 ? 1 : currPage - 1);
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClick = (e, data) => {
    navigate("/admin/managemanufacturer/viewuser/", { state: data });
  };

  useEffect(() => {
    (async () => {
      const config = { _page: currPage, _limit: perPage };
      const res = await postData1(
        "admin/manufacturer_list/",
        config,
        true,
        true
      );
      setManlistData(res?.data?.data);
      setPaging({
        total: res?.data?.total,
        total_pages: res?.data?.total_pages,
      });
      setLoader(false);
    })();
  }, [currPage, perPage]);

  return (
    <section className={styles.userListCont}>
      <div className={styles.listHead}>
        <h1>MANUFACTURER MANAGEMENT</h1>
      </div>
      <div className={styles.dataItems}>
        <div className={styles.itemHead}>
          <span>Deal Type</span>
          <span>Buisness Name</span>
          <span>Person</span>
          <span>Email</span>
          <span>Mobile</span>
          <span>City</span>
          <span>Equipment Cat</span>
          <span>Verified</span>
          <span>Action</span>
        </div>

        {manlistData?.map((data, index) => {
          return (
            <div className={styles.itemData} key={index}>
              <span>{data?.product_deal}</span>
              <span>{data?.firm_name}</span>
              <span>{data?.responsible_person}</span>
              <span>{data?.email}</span>
              <span>{data?.phone_no}</span>
              <span>{data?.office_location}</span>
              <span>{data?.equipname_list}</span>
              <span>{data?.status ? "Yes" : "No"}</span>
              <span className={styles.actiontd}>
                <img
                  src={view}
                  title="view"
                  onClick={(e) => handleClick(e, data)}
                  alt="..."
                />
              </span>
            </div>
          );
        })}
      </div>
      <Pagination
        total={paging?.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging?.total_pages}
        updatePage={updatePage}
      />
      {loader && <Loader />}
    </section>
  );
};

export const DistributorList = () => {
  const navigate = useNavigate();
  const [distlistData, setDistlistData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currPage, setCurrPage] = useState(1);
  const [paging, setPaging] = useState({});
  const [prevPage, setPrevPage] = useState(1);
  const [perPage, setPerPage] = useState(8);

  const updatePage = (e) => {
    let name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage(currPage === 1 ? 1 : currPage - 1);
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    (async () => {
      const config = { _page: currPage, _limit: perPage };
      const res = await postData1(
        "admin/distributer_list/",
        config,
        true,
        true
      );
      setDistlistData(res?.data?.data);
      setPaging({
        total: res?.data?.total,
        total_pages: res?.data?.total_pages,
      });
      setLoader(false);
    })();
  }, [currPage, perPage]);

  const handleClick = (e, data) => {
    navigate("/admin/managemanufacturer/viewuser/", { state: data });
  };

  return (
    <section className={styles.userListCont}>
      <div className={styles.listHead}>
        <h1>DISTRIBUTOR MANAGEMENT</h1>
      </div>
      <div className={styles.dataItems}>
        <div className={styles.itemHead}>
          <span>Deal Type</span>
          <span>Buisness Name</span>
          <span>Person</span>
          <span>Email</span>
          <span>Mobile</span>
          <span>City</span>
          <span>Website</span>
          <span>EQUIPMENT Cat</span>
          <span>Verified</span>
          <span>Action</span>
        </div>

        {distlistData?.map((data, index) => {
          return (
            <div className={styles.itemData} key={index}>
              <span>{data?.product_deal}</span>
              <span>{data?.firm_name}</span>
              <span>{data?.responsible_person}</span>
              <span>{data?.email}</span>
              <span>{data?.phone_no}</span>
              <span>{data?.office_location}</span>
              <span>{data?.website_name}</span>
              <span>{data?.equipname_list}</span>
              <span>{data?.status ? "Yes" : "No"}</span>
              <span className={styles.actiontd}>
                <img
                  onClick={(e) => handleClick(e, data)}
                  src={view}
                  title="view"
                  alt="..."
                />
              </span>
            </div>
          );
        })}
      </div>
      <Pagination
        total={paging?.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging?.total_pages}
        updatePage={updatePage}
      />
      {loader && <Loader />}
    </section>
  );
};

export const BlockToggle = () => {
  const [blockTog, setBlockTog] = useState(false);

  const userBlock = () => {
    setBlockTog(!blockTog);
  };

  return (
    <React.Fragment>
      <img
        src={blockTog ? togleA : togleB}
        title={blockTog ? "Unblock" : "Block"}
        onClick={userBlock}
        alt="..."
      />
      {blockTog && <AlertPopup userBlock={userBlock} />}
    </React.Fragment>
  );
};



// import React, { useEffect, useState, useMemo, useRef } from "react";
// import styles from "../assets/css/UserList.module.css";
// import { defaultProfile, togleA, togleB, view } from "../assets/img";
// import { NavLink, useNavigate } from "react-router-dom";
// import { postData1 } from "../../services";
// import { Loader } from "../../components/Loader";
// import { prev_arrow, next_arrow } from "../../assets/images";
// import { DownloadToExcel, Pagination } from "../Common";
// import { AlertPopup } from "../Common";
// import ProductDetailsModal from "../../components/ProductDetailsModal";
// import { UserListPagination } from "./UserListPagination";
// import UserRegisterAdmin from "./UserRegisterAdmin";
// import { useDebounce } from "@uidotdev/usehooks";

// export const UserList = () => {
//   const [userlistData, setUserListData] = useState([]);
//   const [loader, setLoader] = useState(true);
//   const [currPage, setCurrPage] = useState(1);
//   const [paging, setPaging] = useState({});
//   const [prevPage, setPrevPage] = useState(1);
//   const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
//   const [perPage, setPerPage] = useState(50);

//   // ---- Filters ----
//   const [sourceFilter, setSourceFilter] = useState([]);
//   const [customerTypeFilter, setCustomerTypeFilter] = useState([]);
//   const [userTypeFilter, setUserTypeFilter] = useState("");
//   const [dateFrom, setDateFrom] = useState("");
//   const [dateTo, setDateTo] = useState("");
//   const [dateFilterType, setDateFilterType] = useState("created_date"); // "created_date" | "last_login"
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [lastActivityFilter, setLastActivityFilter] = useState([]);
//   const [mobileFilter, setMobileFilter] = useState("");
//   const [customerNameFilter, setCustomerNameFilter] = useState("");
//   const [emailFilter, setEmailFilter] = useState("");

//   // ---- Debounced text filters (avoid excessive API calls while typing) ----
//   const debouncedMobile = useDebounce(mobileFilter, 400);
//   const debouncedName = useDebounce(customerNameFilter, 400);
//   const debouncedEmail = useDebounce(emailFilter, 400);
//   const [cartMin, setCartMin] = useState("");
//   const [cartMax, setCartMax] = useState("");
//   const [qtyMin, setQtyMin] = useState("");
//   const [qtyMax, setQtyMax] = useState("");

//   // ---- Toggle visibility of inline filter inputs ----
//   const [showFilter, setShowFilter] = useState({
//     mobile: false,
//     customerName: false,
//     email: false,
//     cart: false,
//     qty: false,
//   });

//   // ---- Dropdown toggles ----
//   const [showSourceDropdown, setShowSourceDropdown] = useState(false);
//   const [showCustomerTypeDropdown, setShowCustomerTypeDropdown] = useState(false);
//   const [showUserTypeDropdown, setShowUserTypeDropdown] = useState(false);
//   const [showStatusDropdown, setShowStatusDropdown] = useState(false);
//   const [showLastActivityDropdown, setShowLastActivityDropdown] = useState(false);

//   const sourceDropdownRef = useRef(null);
//   const customerTypeDropdownRef = useRef(null);
//   const userTypeDropdownRef = useRef(null);
//   const statusDropdownRef = useRef(null);
//   const lastActivityDropdownRef = useRef(null);

//   // ---- Column visibility ----
//   const ALL_COLUMNS = [
//     { label: "S.No", key: "sno" },
//     { label: "Source", key: "source" },
//     { label: "Account Created", key: "created_date" },
//     { label: "User ID (Mobile No.)", key: "mobile" },
//     { label: "Customer Name", key: "first_name" },
//     { label: "Email ID", key: "email" },
//     { label: "Customer Type", key: "profile_type" },
//     { label: "Purpose", key: "last_activity" },
//     { label: "Last Visited", key: "last_visit" },
//     { label: "Speciality", key: "speciality" },
//     { label: "Location", key: "location" },
//     { label: "Last Login", key: "last_login" },
//     { label: "Recent Action", key: "recent_action" },
//     { label: "Pop up URL", key: "popup_url" },
//     { label: "Need Help", key: "need_help" },
//     { label: "Brochure Download", key: "catalog" },
//     { label: "Arrange Demo", key: "demo" },
//     { label: "SM Outbound", key: "sm_outbound" },
//     { label: "Add to Cart", key: "cart" },
//     { label: "Checkout Cart", key: "checkout" },
//     { label: "Qty", key: "total_products_purchased" },
//     { label: "Status", key: "status" },
//     { label: "Action", key: "action" },
//   ];
//   const STORAGE_KEY = "userlist_visible_columns";
//   const DEFAULT_VISIBLE = ["S.No", "User ID (Mobile No.)", "Customer Name", "Email ID", "Customer Type", "Status", "Action"];

//   // Restore column visibility from localStorage, or use defaults
//   const getInitialColumns = () => {
//     try {
//       const saved = localStorage.getItem(STORAGE_KEY);
//       if (saved) {
//         const parsed = JSON.parse(saved);
//         // Validate: ensure only valid column labels are restored
//         const validLabels = ALL_COLUMNS.map(c => c.label);
//         const filtered = parsed.filter(l => validLabels.includes(l));
//         if (filtered.length > 0) return filtered;
//       }
//     } catch (e) {
//       // Ignore parse errors
//     }
//     return DEFAULT_VISIBLE;
//   };

//   const [visibleColumns, setVisibleColumns] = useState(getInitialColumns);

//   // Persist column visibility to localStorage whenever it changes
//   useEffect(() => {
//     localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
//   }, [visibleColumns]);
//   const [showColumnSelector, setShowColumnSelector] = useState(false);
//   const columnSelectorRef = useRef(null);

//   const [showProductsModal, setShowProductsModal] = useState(false);
//   const [selectedUserUid, setSelectedUserUid] = useState("");
//   const [modalType, setModalType] = useState("");
//   const [modalTitle, setModalTitle] = useState("");

//   const [downloadData, setDownloadData] = useState([]);
//   const navigate = useNavigate();
//   const [isUserRegOpen, setIsUserRegOpen] = useState(false)

//   // ---- Helper to reset page ----
//   const resetPage = () => {
//     setCurrPage(1);
//     setPrevPage(1);
//   };

//   // ---- Filter handlers ----
//   const toggleSource = (value) => {
//     setSourceFilter((prev) =>
//       prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
//     );
//     resetPage();
//   };

//   const toggleCustomerType = (value) => {
//     setCustomerTypeFilter((prev) =>
//       prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
//     );
//     resetPage();
//   };

//   const toggleLastActivity = (value) => {
//     setLastActivityFilter((prev) =>
//       prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
//     );
//     resetPage();
//   };

//   const clearFilters = () => {
//     setSourceFilter([]);
//     setCustomerTypeFilter([]);
//     setUserTypeFilter("");
//     setLastActivityFilter([]);
//     setDateFrom("");
//     setDateTo("");
//     setDateFilterType("created_date");
//     setStatusFilter("all");
//     setMobileFilter("");
//     setCustomerNameFilter("");
//     setEmailFilter("");
//     setCartMin("");
//     setCartMax("");
//     setQtyMin("");
//     setQtyMax("");
//     setShowFilter({ mobile: false, customerName: false, email: false, cart: false, qty: false });
//     resetPage();
//   };

//   const handleDateFromChange = (e) => { setDateFrom(e.target.value); resetPage(); };
//   const handleDateToChange = (e) => { setDateTo(e.target.value); resetPage(); };
//   const handleDateFilterTypeChange = (e) => { setDateFilterType(e.target.value); resetPage(); };
//   const handleStatusSelect = (value) => { setStatusFilter(value); resetPage(); setShowStatusDropdown(false); };
//   const handleUserTypeSelect = (value) => { setUserTypeFilter(value); resetPage(); setShowUserTypeDropdown(false); };
//   const handleMobileChange = (e) => { setMobileFilter(e.target.value); resetPage(); };
//   const handleCustomerNameChange = (e) => { setCustomerNameFilter(e.target.value); resetPage(); };
//   const handleEmailChange = (e) => { setEmailFilter(e.target.value); resetPage(); };
//   const handleCartMinChange = (e) => { setCartMin(e.target.value); resetPage(); };
//   const handleCartMaxChange = (e) => { setCartMax(e.target.value); resetPage(); };
//   const handleQtyMinChange = (e) => { setQtyMin(e.target.value); resetPage(); };
//   const handleQtyMaxChange = (e) => { setQtyMax(e.target.value); resetPage(); };
//   const handleLastActivitySelect = (value) => { setLastActivityFilter([value]); resetPage(); setShowLastActivityDropdown(false); };

//   // ---- Drag-and-drop reorder state ----
//   const draggedColRef = useRef(null);
//   const [dragOverCol, setDragOverCol] = useState(null);

//   const moveColumn = (fromLabel, toLabel) => {
//     setVisibleColumns((prev) => {
//       const fromIdx = prev.indexOf(fromLabel);
//       const toIdx = prev.indexOf(toLabel);
//       if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
//       const next = [...prev];
//       next.splice(fromIdx, 1);
//       next.splice(toIdx, 0, fromLabel);
//       return next;
//     });
//   };

//   // ---- Column visibility toggle ----
//   const toggleColumn = (label) => {
//     setVisibleColumns((prev) =>
//       prev.includes(label)
//         ? prev.filter((l) => l !== label)
//         : [...prev, label]
//     );
//   };

//   const toggleFilterInput = (key) => {
//     setShowFilter((prev) => ({ ...prev, [key]: !prev[key] }));
//   };

//   // ---- Sorting ----
//   const handleSort = (column) => {
//     let direction = "asc";
//     if (sortConfig.key === column && sortConfig.direction === "asc") {
//       direction = "desc";
//     }
//     setSortConfig({ key: column, direction });
//   };

//   const getValue = (item, key) => {
//     switch (key) {
//       case "first_name": return item?.profile?.first_name || "";
//       case "profile_type": return item?.profile?.profile_type || "";
//       case "location": return item?.profile?.location || "";
//       case "cart_products_count": return item?.cart_products_count || 0;
//       case "total_products_purchased": return item?.total_products_purchased || 0;
//       case "last_login": return item?.last_login || "";
//       case "last_visit": return item?.last_visit || "";
//       case "last_activity": return item?.last_activity || "";
//       default: return item?.[key] || "";
//     }
//   };

//   // ---- Client‑side filtering with enhanced sort for dates ----
//   const filteredData = useMemo(() => {
//     let data = [...userlistData];

//     // Source
//     if (sourceFilter.length > 0) {
//       data = data.filter((item) =>
//         sourceFilter.includes(item?.profile?.signup_source?.toLowerCase())
//       );
//     }

//     // Customer Type (profile_type)
//     if (customerTypeFilter.length > 0) {
//       data = data.filter((item) =>
//         customerTypeFilter.includes(item?.profile?.profile_type?.toLowerCase())
//       );
//     }

//     // User Type (usertype)
//     if (userTypeFilter) {
//       const term = userTypeFilter.toLowerCase();
//       data = data.filter((item) =>
//         (item?.user_type || "").toString().toLowerCase() === term
//       );
//     }

//     // Date range (filter on selected date field: created_date / last_login)
//     if (dateFrom) {
//       data = data.filter((item) => item[dateFilterType] && new Date(item[dateFilterType]) >= new Date(dateFrom));
//     }
//     if (dateTo) {
//       const end = new Date(dateTo);
//       end.setHours(23, 59, 59, 999);
//       data = data.filter((item) => item[dateFilterType] && new Date(item[dateFilterType]) <= end);
//     }

//     // Status filter
//     if (statusFilter !== "all") {
//       const threeMonthsAgo = new Date();
//       threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
//       data = data.filter((item) => {
//         const lastLogin = item.last_login ? new Date(item.last_login) : null;
//         const isActive = lastLogin && lastLogin >= threeMonthsAgo;
//         return statusFilter === "active" ? isActive : !isActive;
//       });
//     }

//     // Last Activity filter
//     if (lastActivityFilter.length > 0) {
//       data = data.filter((item) =>
//         lastActivityFilter.includes(item?.last_activity || "")
//       );
//     }

//     // Customer Name
//     if (customerNameFilter) {
//       const term = customerNameFilter.toLowerCase();
//       data = data.filter((item) =>
//         (item?.profile?.first_name || "").toLowerCase().includes(term)
//       );
//     }

//     // Email
//     if (emailFilter) {
//       const term = emailFilter.toLowerCase();
//       data = data.filter((item) =>
//         (item?.email || "").toLowerCase().includes(term)
//       );
//     }

//     // Mobile
//     if (mobileFilter) {
//       const term = mobileFilter.trim().toLowerCase();
//       data = data.filter((item) =>
//         String(item?.mobile || "").toLowerCase().includes(term)
//       );
//     }
//     // console.log("dddddddd", data)

//     // Add to Cart
//     if (cartMin) {
//       const min = parseInt(cartMin, 10);
//       data = data.filter((item) => (item?.cart_products_count || 0) >= min);
//     }
//     if (cartMax) {
//       const max = parseInt(cartMax, 10);
//       data = data.filter((item) => (item?.cart_products_count || 0) <= max);
//     }

//     // Qty
//     if (qtyMin) {
//       const min = parseInt(qtyMin, 10);
//       data = data.filter((item) => (item?.total_products_purchased || 0) >= min);
//     }
//     if (qtyMax) {
//       const max = parseInt(qtyMax, 10);
//       data = data.filter((item) => (item?.total_products_purchased || 0) <= max);
//     }

//     // Sorting (with special handling for dates)
//     if (sortConfig.key) {
//       data.sort((a, b) => {
//         const rawA = getValue(a, sortConfig.key);
//         const rawB = getValue(b, sortConfig.key);
//         if (["last_login", "created_date", "last_visit"].includes(sortConfig.key)) {
//           const dateA = rawA ? new Date(rawA).getTime() : 0;
//           const dateB = rawB ? new Date(rawB).getTime() : 0;
//           return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
//         }
//         // Numeric comparison for counts
//         if (typeof rawA === "number" && typeof rawB === "number") {
//           return sortConfig.direction === "asc" ? rawA - rawB : rawB - rawA;
//         }
//         // Default string comparison
//         if (rawA < rawB) return sortConfig.direction === "asc" ? -1 : 1;
//         if (rawA > rawB) return sortConfig.direction === "asc" ? 1 : -1;
//         return 0;
//       });
//     }

//     return data;
//   }, [
//     userlistData,
//     sourceFilter,
//     customerTypeFilter,
//     userTypeFilter,
//     dateFrom,
//     dateTo,
//     dateFilterType,
//     statusFilter,
//     lastActivityFilter,
//     customerNameFilter,
//     mobileFilter,
//     emailFilter,
//     cartMin,
//     cartMax,
//     qtyMin,
//     qtyMax,
//     sortConfig,
//   ]);

//   // ---- Helper: format source label ----
//   const formatSourceLabel = (source) => {
//     if (!source) return "";
//     return source === "direct" ? "Equipmedy" : source;
//   };

//   // ---- Helper: compute active/inactive status (last 3 months) ----
//   const computeStatus = (item) => {
//     const lastLogin = item?.last_login ? new Date(item.last_login) : null;
//     const threeMonthsAgo = new Date();
//     threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
//     return lastLogin && lastLogin >= threeMonthsAgo ? "Active" : "Inactive";
//   };

//   // ---- Options for dropdowns ----
//   const sourceOptions = useMemo(() => {
//     const values = new Set();
//     userlistData.forEach((item) => {
//       const raw = item?.profile?.signup_source;
//       if (raw) values.add(raw);
//     });
//     return [...values]
//       .map((value) => ({ value, label: formatSourceLabel(value) }))
//       .sort((a, b) => a.label.localeCompare(b.label));
//   }, [userlistData]);

//   const customerTypeOptions = ["doctor", "hospital", "manufacturer", "distributor"];

//   const lastActivityOptions = useMemo(() => {
//     const values = new Set();
//     userlistData.forEach((item) => {
//       const raw = item?.last_activity;
//       if (raw) values.add(raw);
//     });
//     return [...values].sort((a, b) => a.localeCompare(b));
//   }, [userlistData]);

//   const userTypeOptions = useMemo(() => {
//     const values = new Set();
//     userlistData.forEach((item) => {
//       const raw = item?.user_type;
//       if (raw !== undefined && raw !== null) values.add(raw.toString());
//     });
//     return [...values].sort((a, b) => a.localeCompare(b));
//   }, [userlistData]);

//   // ---- Dynamic header labels ----
//   const sourceHeaderLabel = useMemo(() => {
//     if (sourceFilter.length === 0) return "Source";
//     if (sourceFilter.length === 1) {
//       const found = sourceOptions.find((opt) => opt.value === sourceFilter[0]);
//       return found ? `Source: ${found.label}` : "Source";
//     }
//     return `Source (${sourceFilter.length})`;
//   }, [sourceFilter, sourceOptions]);

//   const customerTypeHeaderLabel = useMemo(() => {
//     if (customerTypeFilter.length === 0) return "Customer Type";
//     if (customerTypeFilter.length === 1) {
//       const found = customerTypeFilter[0];
//       return found ? `Type: ${found}` : "Customer Type";
//     }
//     return `Type (${customerTypeFilter.length})`;
//   }, [customerTypeFilter]);

//   const lastActivityHeaderLabel = useMemo(() => {
//     if (lastActivityFilter.length === 0) return "Purpose";
//     if (lastActivityFilter.length === 1) {
//       return `Purpose: ${lastActivityFilter[0]}`;
//     }
//     return `Purpose (${lastActivityFilter.length})`;
//   }, [lastActivityFilter]);

//   const userTypeHeaderLabel = useMemo(() => {
//     if (!userTypeFilter) return "User Type";
//     return `User Type: ${userTypeFilter}`;
//   }, [userTypeFilter]);

//   // ---- Click‑outside for dropdowns ----
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(event.target)) {
//         setShowSourceDropdown(false);
//       }
//       if (customerTypeDropdownRef.current && !customerTypeDropdownRef.current.contains(event.target)) {
//         setShowCustomerTypeDropdown(false);
//       }
//       if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
//         setShowStatusDropdown(false);
//       }
//       if (lastActivityDropdownRef.current && !lastActivityDropdownRef.current.contains(event.target)) {
//         setShowLastActivityDropdown(false);
//       }
//       if (userTypeDropdownRef.current && !userTypeDropdownRef.current.contains(event.target)) {
//         setShowUserTypeDropdown(false);
//       }
//       if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
//         setShowColumnSelector(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ---- AbortController ref to cancel in‑flight requests ----
//   const abortControllerRef = useRef(null);

//   // ---- Data fetch (API) ----
//   useEffect(() => {
//     // Guard: Don't fetch until text filters have settled (debounce completed)
//     if (mobileFilter !== debouncedMobile) return;
//     if (customerNameFilter !== debouncedName) return;
//     if (emailFilter !== debouncedEmail) return;

//     // Cancel any previous in‑flight request
//     if (abortControllerRef.current) {
//       abortControllerRef.current.abort();
//     }
//     const controller = new AbortController();
//     abortControllerRef.current = controller;

//     (async () => {
//       setLoader(true);
//       const config = {
//         // user_type: 2,
//         _page: currPage,
//         _limit: perPage,
//       };
//       // Server‑side filter params (use debounced values for text inputs)
//       if (customerTypeFilter.length) config.profile_type = customerTypeFilter.join(",");
//       if (userTypeFilter) config.usertype = userTypeFilter;
//       if (debouncedMobile) config.mobile = debouncedMobile;
//       if (debouncedName) config.first_name = debouncedName;
//       if (sourceFilter.length) config.source = sourceFilter.join(",");
//       if (dateFrom) config.from_date = dateFrom;
//       if (dateTo) config.to_date = dateTo;
//       if (dateFrom || dateTo) config.date_filter = dateFilterType;
//       if (statusFilter !== "all") config.status = statusFilter;
//       if (debouncedEmail) config.email = debouncedEmail;
//       if (cartMin) config.cart_min = cartMin;
//       if (cartMax) config.cart_max = cartMax;
//       if (qtyMin) config.qty_min = qtyMin;
//       if (qtyMax) config.qty_max = qtyMax;

//       const res = await postData1("admin/user_list/", config, true, true);

//       // Ignore response if this request was superseded
//       if (controller.signal.aborted) return;

//       const users = res?.data?.data || [];
//       setUserListData(users);

//       const excelData = users.map((item) => ({
//         Source: formatSourceLabel(item?.profile?.signup_source),
//         "Account Created": item?.created_date,
//         Mobile: item?.mobile,
//         Name: item?.profile?.first_name,
//         Email: item?.email,
//         "Customer Type": item?.profile?.profile_type,
//         "User Type": item?.user_type,
//         Location: item?.profile?.location,
//         "Add to Cart": item?.cart_products_count || 0,
//         Qty: item?.total_products_purchased || 0,
//         Status: computeStatus(item),
//         "Last Login": item?.last_login,
//       }));
//       setDownloadData(excelData);

//       setPaging({
//         total: res?.data?.total,
//         total_pages: res?.data?.total_pages,
//       });
//       setLoader(false);
//     })();

//     // Cleanup: abort on unmount
//     return () => controller.abort();
//   }, [
//     currPage,
//     perPage,
//     sourceFilter,
//     customerTypeFilter,
//     userTypeFilter,
//     dateFrom,
//     dateTo,
//     dateFilterType,
//     statusFilter,
//     lastActivityFilter,
//     // Raw text filter values (triggers guard when user types)
//     mobileFilter,
//     customerNameFilter,
//     emailFilter,
//     // Debounced text filter values (triggers actual fetch after settling)
//     debouncedMobile,
//     debouncedName,
//     debouncedEmail,
//     cartMin,
//     cartMax,
//     qtyMin,
//     qtyMax,
//   ]);

//   // ---- Sort Icon (clickable without toggling filter) ----
//   const SortIcon = ({ column }) => {
//     const isActive = sortConfig.key === column;
//     return (
//       <span
//         className="inline-flex flex-col ml-1 opacity-50 group-hover:opacity-100 transition-opacity cursor-pointer"
//         onClick={(e) => {
//           e.stopPropagation();
//           handleSort(column);
//         }}
//       >
//         <svg
//           className={`w-2.5 h-2.5 -mb-0.5 ${isActive && sortConfig.direction === "asc" ? "text-teal-600 opacity-100" : ""}`}
//           fill="currentColor"
//           viewBox="0 0 16 16"
//         >
//           <path d="M8 4l4 6H4z" />
//         </svg>
//         <svg
//           className={`w-2.5 h-2.5 ${isActive && sortConfig.direction === "desc" ? "text-teal-600 opacity-100" : ""}`}
//           fill="currentColor"
//           viewBox="0 0 16 16"
//         >
//           <path d="M8 12L4 6h8z" />
//         </svg>
//       </span>
//     );
//   };

//   const truncate = (str, n) => str && str.length > n ? `${str.substring(0, n)}…` : str;

//   // ---- Column definitions ----
//   const columns = [
//     { label: "S.No", sortKey: null, width: "w-14", noFilter: true },
//     { label: "Source", sortKey: null, width: "w-28", isFilter: "source" },
//     { label: "Account Created", sortKey: "created_date", width: "w-32" },
//     { label: "User ID (Mobile No.)", sortKey: "mobile", width: "w-44", isFilter: "text", filterKey: "mobile" },
//     { label: "Customer Name", sortKey: "first_name", width: "w-40", isFilter: "text", filterKey: "customerName" },
//     { label: "Email ID", sortKey: "email", width: "w-56", isFilter: "text", filterKey: "email" },
//     { label: "Customer Type", sortKey: null, width: "w-36", isFilter: "customerType" },
//     // { label: "User Type", sortKey: null, width: "w-28", isFilter: "userType" },
//     { label: "Purpose", sortKey: null, width: "w-24", isFilter: "lastActivity" },
//     { label: "Last Visited", sortKey: "last_visit", width: "w-36", noFilter: true },
//     { label: "Speciality", sortKey: null, width: "w-20", noFilter: true },
//     { label: "Location", sortKey: "location", width: "w-32" },
//     { label: "Last Login", sortKey: "last_login", width: "w-28", noFilter: true },
//     { label: "Recent Action", sortKey: null, width: "w-24", noFilter: true },
//     { label: "Pop up URL", sortKey: null, width: "w-44", noFilter: true },
//     { label: "Need Help", sortKey: null, width: "w-28", noFilter: true },
//     { label: "Brochure Download", sortKey: null, width: "w-44", noFilter: true },
//     { label: "Arrange Demo", sortKey: null, width: "w-36", noFilter: true },
//     { label: "SM Outbound", sortKey: null, width: "w-32", noFilter: true },
//     { label: "Add to Cart", sortKey: "cart_products_count", width: "w-40", isFilter: "cart", filterKey: "cart" },
//     { label: "Checkout Cart", sortKey: null, width: "w-40", noFilter: true },
//     { label: "Qty", sortKey: "total_products_purchased", width: "w-16", isFilter: "qty", filterKey: "qty" },
//     { label: "Status", sortKey: null, width: "w-32", isFilter: "status" },
//     { label: "Action", sortKey: null, width: "w-28", noFilter: true },
//   ];

//   const filtersActive =
//     sourceFilter.length > 0 ||
//     customerTypeFilter.length > 0 ||
//     userTypeFilter ||
//     lastActivityFilter.length > 0 ||
//     dateFrom ||
//     dateTo ||
//     statusFilter !== "all" ||
//     mobileFilter ||
//     customerNameFilter ||
//     emailFilter ||
//     cartMin ||
//     cartMax ||
//     qtyMin ||
//     qtyMax;

//   // ---- Column Renderers (for dynamic body) ----
//   const columnRenderers = {
//     "S.No": (data, index) => (
//       <td key="S.No" className="px-4 py-3.5 text-sm font-medium text-gray-400 border-r border-gray-50 w-14 text-center">
//         {(currPage - 1) * perPage + index + 1}
//       </td>
//     ),
//     "Source": (data, index) => (
//       <td key="Source" className="px-4 py-3.5 border-r border-gray-50 w-28 text-center">
//         <span className="inline-block min-w-[90px] px-3 py-1 rounded-full text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-600 shadow-sm">
//           {formatSourceLabel(data?.profile?.signup_source)
//             .charAt(0)
//             .toUpperCase() + formatSourceLabel(data?.profile?.signup_source).slice(1)}
//         </span>
//       </td>
//     ),
//     "Account Created": (data, index) => (
//       <td key="Account Created" className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-32 whitespace-nowrap">
//         {data?.created_date ? new Date(data.created_date).toLocaleDateString("en-IN") : "—"}
//       </td>
//     ),
//     "User ID (Mobile No.)": (data, index) => (
//       <td  onClick={(e) => e.stopPropagation()} key="User ID (Mobile No.)" className="px-4 py-3.5 text-sm text-gray-700 font-mono border-r border-gray-50 w-44 whitespace-nowrap">
//         {data?.mobile || "—"}
//       </td>
//     ),
//     "Customer Name": (data, index) => (
//       <td  onClick={(e) => e.stopPropagation()} key="Customer Name" className="px-4 py-3.5 border-r border-gray-50 w-40">
//         <div className="flex items-center gap-2.5">
//           <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
//             {data?.profile?.first_name?.[0]?.toUpperCase() || "?"}
//           </div>
//           <span className="text-sm font-medium text-gray-800 truncate" title={data?.profile?.first_name}>
//             {data?.profile?.first_name || "—"}
//           </span>
//         </div>
//       </td>
//     ),
//     "Email ID": (data, index) => (
//       <td  onClick={(e) => e.stopPropagation()} key="Email ID" className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-56">
//         <span title={data?.email} className="truncate block max-w-[200px]">
//           {truncate(data?.email, 28) || "—"}
//         </span>
//       </td>
//     ),
//     "Customer Type": (data, index) => (
//       <td key="Customer Type" className="px-4 py-3.5 border-r border-gray-50 w-36 text-center">
//         {data?.profile?.profile_type ? (
//           <span
//             className={`inline-block min-w-[100px] px-3 py-1 rounded-full text-sm font-medium border capitalize ${data.profile.profile_type === "doctor"
//               ? "bg-blue-100 text-blue-700 border-blue-500"
//               : data.profile.profile_type === "hospital"
//                 ? "bg-purple-100 text-purple-700 border-purple-500"
//                 : data.profile.profile_type === "manufacturer"
//                   ? "bg-orange-100 text-orange-700 border-orange-500"
//                   : "bg-teal-100 text-teal-700 border-teal-500"
//               }`}
//           >
//             {data.profile.profile_type}
//           </span>
//         ) : (
//           <span className="text-gray-400 text-sm">—</span>
//         )}
//       </td>
//     ),
//     "Purpose": (data, index) => (
//       <td key="Purpose" className="px-4 py-3.5 border-r border-gray-50 w-32 text-sm text-gray-700">
//         {data?.last_activity ? (
//           <span className="inline-block max-w-[180px] truncate" title={data.last_activity}>
//             {data.last_activity}
//           </span>
//         ) : (
//           <span className="text-gray-400">—</span>
//         )}
//       </td>
//     ),
//     "Last Visited": (data, index) => (
//       <td key="Last Visited" className="px-4 py-3.5 text-sm text-gray-500 border-r border-gray-50 whitespace-nowrap">
//         {data?.last_visit
//           ? new Date(data.last_visit).toLocaleString("en-IN", {
//             day: "numeric",
//             month: "numeric",
//             year: "numeric",
//             hour: "2-digit",
//             minute: "2-digit",
//             second: "2-digit",
//             hour12: true,
//           })
//           : "—"}
//       </td>
//     ),
//     "Speciality": (data, index) => (
//       <td key="Speciality" className="px-4 py-3.5 border-r border-gray-50 w-52">
//         <div className="flex flex-wrap gap-1">
//           {data?.profile?.speciality_names?.length > 0 ? (
//             data.profile.speciality_names.map((item, idx) => (
//               <span
//                 key={idx}
//                 className="inline-block px-3 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-700 border-blue-500"
//               >
//                 {item}
//               </span>
//             ))
//           ) : (
//             <span className="text-gray-400">—</span>
//           )}
//         </div>
//       </td>
//     ),
//     "Location": (data, index) => (
//       <td key="Location" className="px-4 py-3.5 text-sm text-gray-600 border-r border-gray-50 w-52">
//         <span title={data?.profile?.location} className="block truncate max-w-[250px]">
//           {truncate(data?.profile?.location, 38) || "—"}
//         </span>
//       </td>
//     ),
//     "Last Login": (data, index) => (
//       <td key="Last Login" className="px-4 py-3.5 text-sm text-gray-500 border-r border-gray-50 whitespace-nowrap">
//         {data?.last_login
//           ? new Date(data.last_login).toLocaleString("en-IN", {
//             day: "numeric",
//             month: "numeric",
//             year: "numeric",
//             hour: "2-digit",
//             minute: "2-digit",
//             second: "2-digit",
//             hour12: true,
//           })
//           : "—"}
//       </td>
//     ),
//     "Recent Action": (data, index) => (
//       <td key="Recent Action" className="px-4 py-3.5 border-r border-gray-50 w-44">
//         {data?.recent_action?.page_url ? (
//           <a
//             href={`${process.env.REACT_APP_FRONTEND_URL}${data.recent_action.page_url}`}
//             target="_blank"
//             rel="noopener noreferrer"
//             className="text-xs text-teal-600 hover:text-teal-800 hover:underline block truncate max-w-[180px]"
//             title={data.recent_action.page_url}
//           >
//             View Page
//           </a>
//         ) : (
//           "—"
//         )}
//       </td>
//     ),
//     "Pop up URL": (data, index) => (
//       <td key="Pop up URL" className="px-4 py-3.5 border-r border-gray-50 w-44">
//         {data?.popup_url ? (
//           <a
//             href={data.popup_url}
//             target="_blank"
//             rel="noopener noreferrer"
//             className="text-xs text-teal-600 hover:text-teal-800 hover:underline truncate block max-w-[160px]"
//             title={data.popup_url}
//           >
//             Latest Popup URL
//           </a>
//         ) : (
//           <span className="text-gray-400">—</span>
//         )}
//       </td>
//     ),
//     "Need Help": (data, index) => (
//       <td key="Need Help" className="px-4 py-3.5 border-r border-gray-50 w-32">
//         <div className="relative group inline-block">
//           <span className="inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border bg-violet-100 text-violet-700 border-violet-500 cursor-pointer text-center">
//             {Object.values(data?.need_help_stats || {}).reduce((sum, val) => sum + val, 0)} Clicks
//           </span>
//           <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
//             <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[220px]">
//               <h4 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-2">Need Help Actions</h4>
//               {Object.entries(data?.need_help_stats || {}).map(([action, count]) => (
//                 <div key={action} className="flex items-center justify-between py-1">
//                   <span className="capitalize text-sm text-gray-600">{action.replaceAll("_", " ")}</span>
//                   <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">{count}</span>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </td>
//     ),
//     "Brochure Download": (data, index) => (
//       <td key="Brochure Download" className="px-4 py-3.5 border-r border-gray-50 w-44">
//         {data?.latest_catalog ? (
//           <a
//             href={`${process.env.REACT_APP_FRONTEND_URL}/${data.latest_catalog.product_slug}`}
//             className="block text-xs text-teal-600 hover:text-teal-800 hover:underline truncate max-w-[160px]"
//             title={data.latest_catalog.product_name}
//             target="_blank"
//           >
//             {data.latest_catalog.product_name}
//           </a>
//         ) : (
//           <span className="text-gray-400">—</span>
//         )}
//       </td>
//     ),
//     "Arrange Demo": (data, index) => (
//       <td key="Arrange Demo" className="px-4 py-3.5 border-r border-gray-50 w-44">
//         {data?.latest_demo_url ? (
//           <a
//             href={`${process.env.REACT_APP_FRONTEND_URL}/${data.latest_demo_url.product_slug}`}
//             className="block text-xs text-teal-600 hover:text-teal-800 hover:underline truncate max-w-[160px]"
//             title={data.latest_demo_url.product_name}
//             target="_blank"
//           >
//             {data.latest_demo_url.product_name}
//           </a>
//         ) : (
//           <span className="text-gray-400">—</span>
//         )}
//       </td>
//     ),
//     "SM Outbound": (data, index) => (
//       <td key="SM Outbound" className="px-4 py-3.5 border-r border-gray-50 w-32">
//         <div className="relative group inline-block">
//           <span className="inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border bg-cyan-100 text-cyan-700 border-cyan-500 cursor-pointer text-center">
//             {Object.values(data?.social_media_outbound_stats || {}).reduce((sum, val) => sum + val, 0)} Clicks
//           </span>
//           <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
//             <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[220px]">
//               <h4 className="text-xs font-semibold text-gray-700 mb-2 border-b pb-2">Social Media Outbound</h4>
//               {Object.entries(data?.social_media_outbound_stats || {}).map(([platform, count]) => (
//                 <div key={platform} className="flex items-center justify-between py-1">
//                   <span className="capitalize text-sm text-gray-600">{platform}</span>
//                   <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-xs font-medium">{count}</span>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </td>
//     ),
//     "Add to Cart": (data, index) => (
//       <td key="Add to Cart" className="px-4 py-3.5 border-r border-gray-50 w-40">
//         <button
//           onClick={(e) => {
//             e.stopPropagation();
//             setSelectedUserUid(data.uid);
//             setModalType("cart");
//             setModalTitle("Add To Cart Products");
//             setShowProductsModal(true);
//           }}
//           className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 font-medium transition-colors"
//         >
//           {(data?.cart_products_count || 0)} Products
//         </button>
//       </td>
//     ),
//     "Checkout Cart": (data, index) => (
//       <td key="Checkout Cart" className="px-4 py-3.5 border-r border-gray-50 w-40">
//         <button
//           onClick={(e) => {
//             e.stopPropagation();
//             setSelectedUserUid(data.uid);
//             setModalType("checkout");
//             setModalTitle("Checkout Products");
//             setShowProductsModal(true);
//           }}
//           className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 font-medium transition-colors"
//         >
//           {(data?.checkout_products_count || 0)} Products
//         </button>
//       </td>
//     ),
//     "Qty": (data, index) => (
//       <td key="Qty" className="px-4 py-3.5 border-r border-gray-50 w-20 text-center">
//         <span
//           className={`inline-flex items-center justify-center min-w-[42px] px-3 py-1 rounded-full text-sm font-bold text-white shadow-sm ${(data?.total_products_purchased || 0) >= 10
//             ? "bg-green-600"
//             : (data?.total_products_purchased || 0) >= 5
//               ? "bg-blue-600"
//               : (data?.total_products_purchased || 0) > 0
//                 ? "bg-violet-600"
//                 : "bg-gray-400"
//             }`}
//         >
//           {data?.total_products_purchased || 0}
//         </span>
//       </td>
//     ),
//     "Status": (data, index) => {
//       const lastLogin = data?.last_login ? new Date(data.last_login) : null;
//       const threeMonthsAgo = new Date();
//       threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
//       const isActive = lastLogin && lastLogin >= threeMonthsAgo;
//       return (
//         <td key="Status" className="px-4 py-3.5 border-r border-gray-50 w-32 text-center">
//           <span
//             className={`inline-block min-w-[80px] px-3 py-1 rounded-full text-sm font-medium border transition-all ${isActive
//               ? "bg-teal-100 text-teal-700 border-teal-500"
//               : "bg-red-100 text-red-500 border-red-500"
//               }`}
//           >
//             {isActive ? "Active" : "Inactive"}
//           </span>
//         </td>
//       );
//     },
//     "Action": (data, index) => (
//       <td key="Action" className="px-4 py-3.5 w-28">
//         <div className="flex items-center gap-1">
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               navigate(`/admin/manageusers/viewuser/${data.uid}`);
//             }}
//             className="p-2 rounded-lg hover:bg-teal-50 text-gray-400 hover:text-teal-600 transition-colors group"
//             title="View user"
//           >
//             <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
//             </svg>
//           </button>
//           {/* {console.log("first", data)} */}
//         </div>
//       </td>
//     ),
//   };

//   // ---- Render ----
//   return (
//     <section className="p-6 bg-white min-h-screen">
//       {/* Unified Header & Filter Toolbar */}
//       <div className="mb-6 ">
//         <div className="flex flex-row items-center justify-between gap-4">
//           {/* Left: Title & Stats */}
//           <div className="flex flex- items-center gap-4">
//             <div>
//               <h1 className="text-2xl font-extrabold text-gray-800 tracking-tight">
//                 User Management
//               </h1>
//               <p className="text-sm text-gray-500 mt-0.5">
//                 Manage and monitor all registered users
//               </p>
//             </div>
//             {/* <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
//         Total: <span className="font-semibold text-gray-700">{paging?.total ?? "—"}</span>
//       </span> */}
//           </div>

//           {/* Right: Date filters + Clear */}
//           <div className="flex flex-row items-center gap-3">
//             <div className="flex items-center gap-1">
//               <button
//                 onClick={() => { setIsUserRegOpen(true) }}
//                 className="text-xs text-gray-500 font-medium px-2 py-2 rounded-md bg-[#019c81] text-white">Register User</button>

//             </div>
//             {/* Date filter type dropdown */}
//             <div className="flex items-center gap-1">
//               {/* <label className="text-xs text-gray-500 font-medium">Filter By</label> */}
//               <select
//                 value={dateFilterType}
//                 onChange={handleDateFilterTypeChange}
//                 className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none"
//                 title="Select which date to filter on"
//               >
//                 <option value="created_date">Account Created</option>
//                 <option value="last_login">Last Login</option>
//               </select>
//             </div>
//             {/* Account From */}
//             <div className="flex items-center gap-1">
//               <label className="text-xs text-gray-500 font-medium">From</label>
//               <input
//                 type="date"
//                 value={dateFrom}
//                 onChange={handleDateFromChange}
//                 className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none w-36"
//               />
//             </div>
//             {/* Account To */}
//             <div className="flex items-center gap-1">
//               <label className="text-xs text-gray-500 font-medium">To</label>
//               <input
//                 type="date"
//                 value={dateTo}
//                 onChange={handleDateToChange}
//                 className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-teal-500 focus:outline-none w-36"
//               />
//             </div>

//             {/* Clear Filters (only when active) */}
//             {filtersActive && (
//               <button
//                 onClick={clearFilters}
//                 className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors shadow-sm"
//               >
//                 Clear Filters
//               </button>
//             )}

//             {/* Column Visibility Button */}
//             <div className="relative" ref={columnSelectorRef}>
//               <button
//                 onClick={() => setShowColumnSelector((prev) => !prev)}
//                 className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors shadow-sm"
//                 title="Toggle columns"
//               >
//                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
//                 </svg>
//                 Columns
//                 <span className="ml-0.5 bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded-full">
//                   {visibleColumns.length}/{ALL_COLUMNS.length}
//                 </span>
//               </button>

//               {showColumnSelector && (
//                 <div
//                   className="absolute right-0 top-full mt-2 z-50 w-56 rounded-2xl border border-gray-200 bg-white shadow-xl"
//                   onClick={(e) => e.stopPropagation()}
//                 >
//                   <div className="px-4 py-2.5 text-xs font-semibold text-gray-500 border-b border-gray-100">
//                     Show / Hide Columns
//                   </div>
//                   <div className="max-h-64 overflow-y-auto py-1">
//                     {/* Show checked (visible) columns first, then unchecked */}
//                     {[...visibleColumns, ...ALL_COLUMNS.filter(c => !visibleColumns.includes(c.label)).map(c => c.label)].map((colLabel) => {
//                       const col = ALL_COLUMNS.find(c => c.label === colLabel);
//                       if (!col) return null;
//                       const checked = visibleColumns.includes(col.label);
//                       const isDragOver = dragOverCol === col.label;
//                       return (
//                         <div
//                           key={col.label}
//                           draggable={checked}
//                           onDragStart={(e) => {
//                             draggedColRef.current = col.label;
//                             e.dataTransfer.effectAllowed = "move";
//                           }}
//                           onDragOver={(e) => {
//                             e.preventDefault();
//                             e.dataTransfer.dropEffect = "move";
//                             setDragOverCol(col.label);
//                           }}
//                           onDragLeave={() => {
//                             if (dragOverCol === col.label) setDragOverCol(null);
//                           }}
//                           onDrop={(e) => {
//                             e.preventDefault();
//                             const from = draggedColRef.current;
//                             const to = col.label;
//                             if (from && to && from !== to) {
//                               moveColumn(from, to);
//                             }
//                             draggedColRef.current = null;
//                             setDragOverCol(null);
//                           }}
//                           onDragEnd={() => {
//                             draggedColRef.current = null;
//                             setDragOverCol(null);
//                           }}
//                           className={`flex items-center w-full px-4 py-2 text-sm text-left transition-colors ${checked ? "text-teal-700 font-medium" : "text-gray-500"
//                             } ${isDragOver ? "bg-teal-50 border-t-2 border-teal-400" : "border-t border-transparent"
//                             } ${checked ? "cursor-grab active:cursor-grabbing hover:bg-gray-50" : ""
//                             }`}
//                           onClick={() => {
//                             if (checked && draggedColRef.current) return; // don't toggle during drag
//                             toggleColumn(col.label);
//                           }}
//                         >
//                           {checked && (
//                             <svg className="w-3.5 h-3.5 mr-1 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16">
//                               <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 2a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 5a1 1 0 1 0-2 0 1 1 0 0 0 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 8a1 1 0 1 0-2 0 1 1 0 0 0 2 0z" />
//                             </svg>
//                           )}
//                           <input
//                             type="checkbox"
//                             checked={checked}
//                             onChange={() => {}}
//                             className="mr-2.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
//                           />
//                           <span className="truncate">{col.label}</span>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </div>
//               )}
//             </div>

//             {/* Export Button */}
//             <div className="rounded-lg w-10 h-10 text-white cursor-pointer transition-colors">
//               {/* <DownloadToExcel
//                 data={filteredData.map((item) => {
//                   const row = {};
//                   visibleColumns.forEach((colLabel) => {
//                     switch (colLabel) {
//                       case "S.No":
//                         // Skip S.No — it's a row index, not a data field
//                         break;
//                       case "Action":
//                         // Skip Action — it's a button, not data
//                         break;
//                       case "Source":
//                         row.Source = formatSourceLabel(item?.profile?.signup_source);
//                         break;
//                       case "Account Created":
//                         row["Account Created"] = item?.created_date;
//                         break;
//                       case "User ID (Mobile No.)":
//                         row["Mobile"] = item?.mobile;
//                         break;
//                       case "Customer Name":
//                         row["Name"] = item?.profile?.first_name;
//                         break;
//                       case "Email ID":
//                         row["Email"] = item?.email;
//                         break;
//                       case "Customer Type":
//                         row["Customer Type"] = item?.profile?.profile_type;
//                         break;
//                       case "Purpose":
//                         row["Purpose"] = item?.last_activity;
//                         break;
//                       case "Last Visited":
//                         row["Last Visited"] = item?.last_visit;
//                         break;
//                       case "Speciality":
//                         row["Speciality"] = item?.profile?.speciality_names?.join(", ") || "";
//                         break;
//                       case "Location":
//                         row["Location"] = item?.profile?.location;
//                         break;
//                       case "Last Login":
//                         row["Last Login"] = item?.last_login;
//                         break;
//                       case "Recent Action":
//                         row["Recent Action"] = item?.recent_action?.page_url || "";
//                         break;
//                       case "Pop up URL":
//                         row["Pop up URL"] = item?.popup_url || "";
//                         break;
//                       case "Need Help":
//                         row["Need Help"] = Object.values(item?.need_help_stats || {}).reduce((sum, val) => sum + val, 0);
//                         break;
//                       case "Brochure Download":
//                         row["Brochure Download"] = item?.latest_catalog?.product_name || "";
//                         break;
//                       case "Arrange Demo":
//                         row["Arrange Demo"] = item?.latest_demo_url?.product_name || "";
//                         break;
//                       case "SM Outbound":
//                         row["SM Outbound"] = Object.values(item?.social_media_outbound_stats || {}).reduce((sum, val) => sum + val, 0);
//                         break;
//                       case "Add to Cart":
//                         row["Add to Cart"] = item?.cart_products_count || 0;
//                         break;
//                       case "Checkout Cart":
//                         row["Checkout Cart"] = item?.checkout_products_count || 0;
//                         break;
//                       case "Qty":
//                         row["Qty"] = item?.total_products_purchased || 0;
//                         break;
//                       case "Status":
//                         row["Status"] = computeStatus(item);
//                         break;
//                     }
//                   });
//                   return row;
//                 })}
//                 filename="userlist"
//               /> */}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Table Card */}
//       <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
//         <div className="overflow-auto max-h-[calc(100vh - 300px)]">
//           <table className="border-collapse  w-full" style={{
//             minWidth: visibleColumns.length < 12 ? "auto" : "2800px",
//             width: visibleColumns.length < 12 ? "100%" : "max-content",
//           }}>
//             {/* Sticky header */}
//             <thead>
//               <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
//                 {visibleColumns.map((colLabel) => {
//                   const col = columns.find(c => c.label === colLabel);
//                   if (!col) return null;
//                   const { label, sortKey, width, isFilter, noFilter, filterKey } = col;
//                   const isSource = isFilter === "source";
//                   const isCustomerType = isFilter === "customerType";
//                   const isStatus = isFilter === "status";
//                   const isLastActivity = isFilter === "lastActivity";
//                   const isUserType = isFilter === "userType";
//                   const isText = isFilter === "text";
//                   const isCart = isFilter === "cart";
//                   const isQty = isFilter === "qty";
//                   const hasFilter = isSource || isCustomerType || isUserType || isStatus || isLastActivity || isText || isCart || isQty;
//                   const showFilterInput = filterKey ? showFilter[filterKey] : false;

//                   let displayLabel = label;
//                   if (isSource) displayLabel = sourceHeaderLabel;
//                   if (isCustomerType) displayLabel = customerTypeHeaderLabel;
//                   if (isUserType) displayLabel = userTypeHeaderLabel;
//                   if (isLastActivity) displayLabel = lastActivityHeaderLabel;
//                   if (isStatus) {
//                     displayLabel =
//                       statusFilter === "all"
//                         ? "Status"
//                         : statusFilter === "active"
//                           ? "Status: Active"
//                           : "Status: Inactive";
//                   }

//                   const handleHeaderClick = () => {
//                     if (isText || isCart || isQty) {
//                       toggleFilterInput(filterKey);
//                     } else if (isSource) {
//                       setShowSourceDropdown((prev) => !prev);
//                     } else if (isCustomerType) {
//                       setShowCustomerTypeDropdown((prev) => !prev);
//                     } else if (isUserType) {
//                       setShowUserTypeDropdown((prev) => !prev);
//                     } else if (isLastActivity) {
//                       setShowLastActivityDropdown((prev) => !prev);
//                     } else if (isStatus) {
//                       setShowStatusDropdown((prev) => !prev);
//                     } else if (sortKey && !hasFilter) {
//                       handleSort(sortKey);
//                     }
//                   };

//                   return (
//                     <th
//                       key={label}
//                       ref={
//                         isSource
//                           ? sourceDropdownRef
//                           : isCustomerType
//                             ? customerTypeDropdownRef
//                             : isLastActivity
//                               ? lastActivityDropdownRef
//                               : isUserType
//                                 ? userTypeDropdownRef
//                                 : isStatus
//                                   ? statusDropdownRef
//                                   : null
//                       }
//                       onClick={handleHeaderClick}
//                       className={`
//                         group sticky top-0 z-20 bg-gradient-to-b from-gray-50 to-gray-100
//                         px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider
//                         border-r border-gray-100 last:border-r-0 select-none relative align-top
//                         ${width}
//                         ${(hasFilter || sortKey) && !noFilter
//                           ? "cursor-pointer hover:bg-gray-200 hover:text-teal-700 transition-colors"
//                           : "cursor-default"
//                         }
//                       `}
//                     >
//                       <div className="flex items-center gap-1 whitespace-nowrap">
//                         {/* Inline filter input or label */}
//                         {isText && showFilterInput ? (
//                           <input
//                             type="text"
//                             placeholder={`Filter ${label}`}
//                             value={
//                               filterKey === "mobile"
//                                 ? mobileFilter
//                                 : filterKey === "customerName"
//                                   ? customerNameFilter
//                                   : emailFilter
//                             }
//                             onChange={(e) => {
//                               if (filterKey === "mobile") return handleMobileChange(e);
//                               if (filterKey === "customerName") return handleCustomerNameChange(e);
//                               return handleEmailChange(e);
//                             }}
//                             onClick={(e) => e.stopPropagation()}
//                             className="w-full rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
//                           />
//                         ) : isCart && showFilterInput ? (
//                           <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
//                             <input
//                               type="number"
//                               placeholder="Min"
//                               value={cartMin}
//                               onChange={handleCartMinChange}
//                               className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
//                             />
//                             <input
//                               type="number"
//                               placeholder="Max"
//                               value={cartMax}
//                               onChange={handleCartMaxChange}
//                               className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
//                             />
//                           </div>
//                         ) : isQty && showFilterInput ? (
//                           <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
//                             <input
//                               type="number"
//                               placeholder="Min"
//                               value={qtyMin}
//                               onChange={handleQtyMinChange}
//                               className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
//                             />
//                             <input
//                               type="number"
//                               placeholder="Max"
//                               value={qtyMax}
//                               onChange={handleQtyMaxChange}
//                               className="w-12 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs text-gray-700 placeholder-gray-400 focus:border-teal-500 focus:outline-none"
//                             />
//                           </div>
//                         ) : (
//                           <span>{displayLabel}</span>
//                         )}

//                         {/* Sort icon always visible */}
//                         {sortKey && <SortIcon column={sortKey} />}

//                         {/* Dropdown indicator for source/customer type/status/last activity */}
//                         {hasFilter && !isText && !isCart && !isQty && (
//                           <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
//                           </svg>
//                         )}

//                         {/* Badges for multi‑select */}
//                         {isSource && sourceFilter.length > 0 && (
//                           <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
//                             {sourceFilter.length}
//                           </span>
//                         )}
//                         {isCustomerType && customerTypeFilter.length > 0 && (
//                           <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
//                             {customerTypeFilter.length}
//                           </span>
//                         )}
//                         {isLastActivity && lastActivityFilter.length > 0 && (
//                           <span className="ml-1 bg-teal-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
//                             {lastActivityFilter.length}
//                           </span>
//                         )}
//                       </div>

//                       {/* ---- Dropdown menus (appear below) ---- */}
//                       {isStatus && showStatusDropdown && (
//                         <div
//                           className="absolute left-0 top-full z-50 mt-1 w-40 rounded-2xl border border-gray-200 bg-white shadow-lg"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <button
//                             type="button"
//                             onClick={() => handleStatusSelect("all")}
//                             className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === "all" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
//                               }`}
//                           >
//                             All
//                           </button>
//                           <button
//                             type="button"
//                             onClick={() => handleStatusSelect("active")}
//                             className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === "active" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
//                               }`}
//                           >
//                             Active
//                           </button>
//                           <button
//                             type="button"
//                             onClick={() => handleStatusSelect("inactive")}
//                             className={`block w-full px-4 py-2 text-left text-sm ${statusFilter === "inactive" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
//                               }`}
//                           >
//                             Inactive
//                           </button>
//                         </div>
//                       )}

//                       {isLastActivity && showLastActivityDropdown && (
//                         <div
//                           className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
//                             Select Purpose
//                           </div>
//                           <div className="max-h-48 overflow-y-auto">
//                             {lastActivityOptions.length === 0 ? (
//                               <div className="px-4 py-2 text-sm text-gray-500">No purposes</div>
//                             ) : (
//                               lastActivityOptions.map((option) => {
//                                 const checked = lastActivityFilter.includes(option);
//                                 return (
//                                   <button
//                                     key={option}
//                                     type="button"
//                                     onClick={() => handleLastActivitySelect(option)}
//                                     className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked ? "bg-teal-50 text-teal-700" : "text-gray-700"
//                                       }`}
//                                   >
//                                     <input
//                                       type="checkbox"
//                                       checked={checked}
//                                       onChange={() => { }}
//                                       className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
//                                     />
//                                     {option}
//                                   </button>
//                                 );
//                               })
//                             )}
//                           </div>
//                           <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
//                             <button
//                               type="button"
//                               onClick={() => setShowLastActivityDropdown(false)}
//                               className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
//                             >
//                               Done
//                             </button>
//                           </div>
//                         </div>
//                       )}

//                       {isSource && showSourceDropdown && (
//                         <div
//                           className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
//                             Select Sources
//                           </div>
//                           <div className="max-h-48 overflow-y-auto">
//                             {sourceOptions.length === 0 ? (
//                               <div className="px-4 py-2 text-sm text-gray-500">No sources</div>
//                             ) : (
//                               sourceOptions.map((option) => {
//                                 const checked = sourceFilter.includes(option.value);
//                                 return (
//                                   <button
//                                     key={option.value}
//                                     type="button"
//                                     onClick={() => toggleSource(option.value)}
//                                     className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked ? "bg-teal-50 text-teal-700" : "text-gray-700"
//                                       }`}
//                                   >
//                                     <input
//                                       type="checkbox"
//                                       checked={checked}
//                                       onChange={() => { }}
//                                       className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
//                                     />
//                                     {option.label}
//                                   </button>
//                                 );
//                               })
//                             )}
//                           </div>
//                           <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
//                             <button
//                               type="button"
//                               onClick={() => setShowSourceDropdown(false)}
//                               className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
//                             >
//                               Done
//                             </button>
//                           </div>
//                         </div>
//                       )}

//                       {isCustomerType && showCustomerTypeDropdown && (
//                         <div
//                           className="absolute left-0 top-full z-50 mt-1 w-56 rounded-2xl border border-gray-200 bg-white shadow-lg"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <div className="px-4 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
//                             Select Customer Types
//                           </div>
//                           <div className="max-h-48 overflow-y-auto">
//                             {customerTypeOptions.length === 0 ? (
//                               <div className="px-4 py-2 text-sm text-gray-500">No types</div>
//                             ) : (
//                               customerTypeOptions.map((type) => {
//                                 const checked = customerTypeFilter.includes(type);
//                                 return (
//                                   <button
//                                     key={type}
//                                     type="button"
//                                     onClick={() => toggleCustomerType(type)}
//                                     className={`flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${checked ? "bg-teal-50 text-teal-700" : "text-gray-700"
//                                       }`}
//                                   >
//                                     <input
//                                       type="checkbox"
//                                       checked={checked}
//                                       onChange={() => { }}
//                                       className="mr-2 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
//                                     />
//                                     {type}
//                                   </button>
//                                 );
//                               })
//                             )}
//                           </div>
//                           <div className="border-t border-gray-100 px-4 py-2 flex justify-end">
//                             <button
//                               type="button"
//                               onClick={() => setShowCustomerTypeDropdown(false)}
//                               className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
//                             >
//                               Done
//                             </button>
//                           </div>
//                         </div>
//                       )}

//                       {isUserType && showUserTypeDropdown && (
//                         <div
//                           className="absolute left-0 top-full z-50 mt-1 w-40 rounded-2xl border border-gray-200 bg-white shadow-lg"
//                           onClick={(e) => e.stopPropagation()}
//                         >
//                           <button
//                             type="button"
//                             onClick={() => handleUserTypeSelect("")}
//                             className={`block w-full px-4 py-2 text-left text-sm ${!userTypeFilter ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
//                               }`}
//                           >
//                             All
//                           </button>
//                           {userTypeOptions.map((option) => (
//                             <button
//                               key={option}
//                               type="button"
//                               onClick={() => handleUserTypeSelect(option)}
//                               className={`block w-full px-4 py-2 text-left text-sm ${userTypeFilter === option ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
//                                 }`}
//                             >
//                               {option}
//                             </button>
//                           ))}
//                         </div>
//                       )}
//                     </th>
//                   );
//                 })}
//               </tr>
//             </thead>

//             <tbody className="divide-y divide-gray-50">
//               {filteredData.map((data, index) => (
//                 <tr
//                   key={index}
//                   onClick={() => navigate(`/admin/manageusers/viewuser/${data.uid}`)}
//                   className="hover:bg-teal-50/50 transition-colors cursor-pointer duration-100 even:bg-gray-50/30"
//                 >
//                   {visibleColumns.map((colKey) => {
//                     const renderer = columnRenderers[colKey];
//                     return renderer ? renderer(data, index) : null;
//                   })}
//                 </tr>
//               ))}

//               {!loader && filteredData.length === 0 && (
//                 <tr>
//                   <td colSpan={visibleColumns.length} className="px-6 py-16 text-center">
//                     <div className="flex flex-col items-center gap-2 text-gray-400">
//                       <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
//                           d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
//                       </svg>
//                       <p className="text-sm font-medium">No users found</p>
//                     </div>
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       <Pagination
//         total={paging?.total}
//         perPage={perPage}
//         setPerPage={setPerPage}
//         currPage={currPage}
//         setCurrPage={setCurrPage}
//         prevPage={prevPage}
//         tPage={paging?.total_pages}
//         updatePage={(e) => {
//           const name = e.currentTarget.name;
//           if (name === "prev") {
//             setCurrPage(currPage === 1 ? 1 : currPage - 1);
//             setPrevPage(currPage);
//           } else if (name === "next") {
//             setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
//             setPrevPage(currPage);
//           } else {
//             setCurrPage(parseInt(e.currentTarget.innerHTML));
//           }
//           window.scrollTo({ top: 0, behavior: "smooth" });
//         }}
//       />
//       {loader && <Loader />}
//       <ProductDetailsModal
//         isOpen={showProductsModal}
//         onClose={() => {
//           setShowProductsModal(false);
//           setSelectedUserUid("");
//           setModalType("");
//         }}
//         uid={selectedUserUid}
//         type={modalType}
//         title={modalTitle}
//       />


//       {isUserRegOpen &&
//         <UserRegisterAdmin isOpen={isUserRegOpen}

//           onClose={() => setIsUserRegOpen(false)}
//         />
//       }
//     </section>
//   );
// };

// // ManufacturerList, DistributorList, BlockToggle remain unchanged

// export const ManufacturerList = () => {
//   const navigate = useNavigate();
//   const [manlistData, setManlistData] = useState([]);
//   const [loader, setLoader] = useState(true);
//   const [currPage, setCurrPage] = useState(1);
//   const [paging, setPaging] = useState({});
//   const [prevPage, setPrevPage] = useState(1);
//   const [perPage, setPerPage] = useState(8);

//   const updatePage = (e) => {
//     let name = e.currentTarget.name;
//     if (name === "prev") {
//       setCurrPage(currPage === 1 ? 1 : currPage - 1);
//       setPrevPage(currPage);
//     } else if (name === "next") {
//       setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
//       setPrevPage(currPage);
//     } else {
//       setCurrPage(parseInt(e.currentTarget.innerHTML));
//     }
//     window.scrollTo({
//       top: 0,
//       behavior: "smooth",
//     });
//   };

//   const handleClick = (e, data) => {
//     navigate("/admin/managemanufacturer/viewuser/", { state: data });
//   };

//   useEffect(() => {
//     (async () => {
//       const config = { _page: currPage, _limit: perPage };
//       const res = await postData1(
//         "admin/manufacturer_list/",
//         config,
//         true,
//         true
//       );
//       setManlistData(res?.data?.data);
//       setPaging({
//         total: res?.data?.total,
//         total_pages: res?.data?.total_pages,
//       });
//       setLoader(false);
//     })();
//   }, [currPage, perPage]);

//   return (
//     <section className={styles.userListCont}>
//       <div className={styles.listHead}>
//         <h1>MANUFACTURER MANAGEMENT</h1>
//       </div>
//       <div className={styles.dataItems}>
//         <div className={styles.itemHead}>
//           <span>Deal Type</span>
//           <span>Buisness Name</span>
//           <span>Person</span>
//           <span>Email</span>
//           <span>Mobile</span>
//           <span>City</span>
//           <span>Equipment Cat</span>
//           <span>Verified</span>
//           <span>Action</span>
//         </div>

//         {manlistData?.map((data, index) => {
//           return (
//             <div className={styles.itemData} key={index}>
//               <span>{data?.product_deal}</span>
//               <span>{data?.firm_name}</span>
//               <span>{data?.responsible_person}</span>
//               <span>{data?.email}</span>
//               <span>{data?.phone_no}</span>
//               <span>{data?.office_location}</span>
//               <span>{data?.equipname_list}</span>
//               <span>{data?.status ? "Yes" : "No"}</span>
//               <span className={styles.actiontd}>
//                 <img
//                   src={view}
//                   title="view"
//                   onClick={(e) => handleClick(e, data)}
//                   alt="..."
//                 />
//                 {/* <BlockToggle/> */}
//               </span>
//             </div>
//           );
//         })}
//       </div>
//       <Pagination
//         total={paging?.total}
//         perPage={perPage}
//         setPerPage={setPerPage}
//         currPage={currPage}
//         setCurrPage={setCurrPage}
//         prevPage={prevPage}
//         tPage={paging?.total_pages}
//         updatePage={updatePage}
//       />
//       {loader && <Loader />}
//     </section>
//   );
// };
// export const DistributorList = () => {
//   const navigate = useNavigate();
//   const [distlistData, setDistlistData] = useState([]);
//   const [loader, setLoader] = useState(true);
//   const [currPage, setCurrPage] = useState(1);
//   const [paging, setPaging] = useState({});
//   const [prevPage, setPrevPage] = useState(1);
//   const [perPage, setPerPage] = useState(8);

//   const updatePage = (e) => {
//     let name = e.currentTarget.name;
//     if (name === "prev") {
//       setCurrPage(currPage === 1 ? 1 : currPage - 1);
//       setPrevPage(currPage);
//     } else if (name === "next") {
//       setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
//       setPrevPage(currPage);
//     } else {
//       setCurrPage(parseInt(e.currentTarget.innerHTML));
//     }
//     window.scrollTo({
//       top: 0,
//       behavior: "smooth",
//     });
//   };

//   useEffect(() => {
//     (async () => {
//       const config = { _page: currPage, _limit: perPage };
//       const res = await postData1(
//         "admin/distributer_list/",
//         config,
//         true,
//         true
//       );
//       setDistlistData(res?.data?.data);
//       setPaging({
//         total: res?.data?.total,
//         total_pages: res?.data?.total_pages,
//       });
//       setLoader(false);
//     })();
//   }, [currPage, perPage]);

//   const handleClick = (e, data) => {
//     navigate("/admin/managemanufacturer/viewuser/", { state: data });
//   };

//   return (
//     <section className={styles.userListCont}>
//       <div className={styles.listHead}>
//         <h1>DISTRIBUTOR MANAGEMENT</h1>
//       </div>
//       <div className={styles.dataItems}>
//         <div className={styles.itemHead}>
//           <span>Deal Type</span>
//           <span>Buisness Name</span>
//           <span>Person</span>
//           <span>Email</span>
//           <span>Mobile</span>
//           <span>City</span>
//           <span>Website</span>
//           <span>EQUIPMENT Cat</span>
//           <span>Verified</span>
//           <span>Action</span>
//         </div>

//         {distlistData?.map((data, index) => {
//           return (
//             <div className={styles.itemData} key={index}>
//               <span>{data?.product_deal}</span>
//               <span>{data?.firm_name}</span>
//               <span>{data?.responsible_person}</span>
//               <span>{data?.email}</span>
//               <span>{data?.phone_no}</span>
//               <span>{data?.office_location}</span>
//               <span>{data?.website_name}</span>
//               <span>{data?.equipname_list}</span>
//               <span>{data?.status ? "Yes" : "No"}</span>
//               <span className={styles.actiontd}>
//                 <img
//                   onClick={(e) => handleClick(e, data)}
//                   src={view}
//                   title="view"
//                   alt="..."
//                 />
//                 {/* <BlockToggle/> */}
//               </span>
//             </div>
//           );
//         })}
//       </div>
//       <Pagination
//         total={paging?.total}
//         perPage={perPage}
//         setPerPage={setPerPage}
//         currPage={currPage}
//         setCurrPage={setCurrPage}
//         prevPage={prevPage}
//         tPage={paging?.total_pages}
//         updatePage={updatePage}
//       />
//       {loader && <Loader />}

//     </section>
//   );
// };

// export const BlockToggle = () => {
//   const [blockTog, setBlockTog] = useState(false);

//   const userBlock = () => {
//     setBlockTog(!blockTog);
//   };

//   return (
//     <React.Fragment>
//       <img
//         src={blockTog ? togleA : togleB}
//         title={blockTog ? "Unblock" : "Block"}
//         onClick={userBlock}
//         alt="..."
//       />
//       {blockTog && <AlertPopup userBlock={userBlock} />}
//     </React.Fragment>
//   );
// };
